import * as Logger from './logger.js';
import writeExcelFile from 'write-excel-file';
import { HSComponent } from './component.js';
import { UserSession } from './session.js';
import { HSListPicker } from './list-picker.js'; // eslint-disable-line no-unused-vars
import './toolbar-button.js';
import styles from './explorer.css';
import template from './explorer.html';
import { SlickGrid, SlickDataView } from 'slickgrid';
import { SlickGridUtils } from './slick-grid-utils.js';

// ECharts (tree-shakable)
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
echarts.use([ BarChart, LineChart, TitleComponent, TooltipComponent, GridComponent, LabelLayout, CanvasRenderer, LegendComponent ]);

const OPERATORS = [
    { value: '==',          label: '==' },
    { value: '!=',          label: '!=' },
    { value: '>',           label: '>'  },
    { value: '>=',          label: '>=' },
    { value: '<',           label: '<'  },
    { value: '<=',          label: '<=' },
    { value: 'contains',    label: 'contains' },
    { value: 'not contains', label: 'not contains' },
    { value: 'in',          label: 'in (csv)' },
];

export class HSExplorer extends HSComponent {
    // internal state
    #sources = [];
    #schema = null;
    #sourceSchemaOriginal = null;
    #selectedSource = null;
    #selectedFields = new Set();
    #hasUserFieldSelection = false;
    #filters = [];          // [{ id, field, op, value }]
    #aggrOp = '';
    #aggrField = '';
    #results = [];
    #filterIdCounter = 0;
    #granularity = '';      // hourly, daily, monthly, quarterly
    #adornments = [];       // [{ id, sourceId, keyMapping: [{adornKey, primaryField}], fields }]
    #adornmentIdCounter = 0;
    #autoPreviewDefaults = []; // [{ field, op, value, reason }]
    #pickerFilterId = null;
    #pickerField = '';
    #fieldValueOptionsCache = new Map();
    #showAllFields = true;
    #autoVisibleFieldIds = new Set();
    #activeHeaderField = '';
    #activeHeaderIndex = -1;
    #adornFieldPickerAdornmentId = null;
    #fieldSelectionRefreshTimer = null;
    #resultsView = 'card';
    #schemaViewMode = 'summary';
    #activeControlsSectionId = '';
    #controlsSectionSlots = new Map();

    // Wizard stepper state
    static STEPS = ['source', 'fields', 'time', 'transform', 'visualize', 'customize', 'output'];
    static STEP_INSTRUCTIONS = {
        source: 'Choose the primary data source for this widget.',
        fields: 'Select the fields you want in your widget. Optionally add fields from related sources.',
        time: 'Set the time window for this data. Use a quick preset or expand the custom date picker.',
        transform: 'Apply filters, grouping, and aggregation to shape the data.',
        visualize: 'Choose how to display the data and map fields to chart axes.',
        customize: 'Fine-tune the appearance: column settings, formatting, and sizing.',
        output: 'Your widget configuration is ready. Copy the JSON to use in a dashboard config file.',
    };
    #currentStep = 'source';
    #vizType = 'table';
    #groupByFields = [];
    #groupByIdCounter = 0;
    #columnConfigs = []; // [{ field, label, formatter, width, frozen }]
    #keyMappingAdornmentId = null; // adornment ID currently being configured in key mapping modal
    #keyMappingDraft = []; // working copy of keyMapping for modal editing
    #joinOnlyFields = new Set(); // fields excluded from final output (used only for adornment keys)
    #hiddenSources = new Set(); // source IDs marked as hidden by devops
    #showHiddenSources = false; // toggle to reveal hidden sources in picker
    #canManageHiddenSources = false; // only devops/developer/super can toggle

    // SlickGrid
    #dataView = null;
    #grid = null;

    // ECharts preview
    #previewChart = null;

    constructor() {
        super();
    }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        this.#captureControlsSectionSlots();
        this.#bindPersistentEvents();
        this.#initGrid();
        this.#loadSources();
        this.#syncStepNav();
    }

    onUnload() {
        super.onUnload();
        this.#closeControlsModal();
        this.#disposeChartPreview();
        if (this.#grid) {
            this.#grid.destroy();
            this.#grid = null;
        }
        this.#dataView = null;
    }

    // ── Initialization ──────────────────────────────────────────────────────

    #bindPersistentEvents() {
        const sd = this.shadowRoot;

        // Close
        this.onEvent('click', () => this.#close(), sd.getElementById('close-btn'));
        this.onEvent('click', () => this.#close(), sd.querySelector('.explorer-backdrop'));
        this.onEvent('keydown', (_, ev) => {
            if (ev.key !== 'Escape') {
                return;
            }
            const keyMappingModal = this.shadowRoot.getElementById('adorn-key-mapping-modal');
            if (keyMappingModal && !keyMappingModal.classList.contains('hidden')) {
                ev.preventDefault();
                this.#closeAdornKeyMappingModal();
                return;
            }
            const modal = this.shadowRoot.getElementById('controls-modal');
            if (modal && !modal.classList.contains('hidden')) {
                ev.preventDefault();
                this.#closeControlsModal();
                return;
            }
            this.#close();
        }, document);
        this.onEvent('click', () => this.#hideAdornShortcutMenu(), document);

        // Source picker — trigger button opens modal
        this.onEvent('click', () => this.#openSourcePickerForPrimary(), sd.getElementById('source-picker-trigger'));

        // Actions
        this.onEvent('click', () => this.#runQuery(), sd.getElementById('run-btn'));
        this.onEvent('click', () => this.#exportData(), sd.getElementById('export-btn'));
        this.onEvent('click', () => this.#navigateToStep('source'), sd.getElementById('active-source-crumb'));
        this.onEvent('click', () => this.#closeControlsModal(), sd.getElementById('controls-modal-close'));
        this.onEvent('click', () => this.#closeControlsModal(), sd.querySelector('#controls-modal .provider-picker-backdrop'));
        this.onEvent('keydown', (_, ev) => this.#onGridHeaderKeydown(ev), sd.getElementById('table-target'));
        this.onEvent('click', () => this.#setResultsView('card'), sd.getElementById('results-view-card-btn'));
        this.onEvent('click', () => this.#setResultsView('json'), sd.getElementById('results-view-json-btn'));
        this.onEvent('click', () => this.#setResultsView('schema'), sd.getElementById('results-view-schema-btn'));
        this.onEvent('click', () => this.#setResultsView('config'), sd.getElementById('results-view-config-btn'));
        this.onEvent('click', () => this.#copyJsonView(), sd.getElementById('results-copy-json-btn'));
        this.onEvent('click', () => this.#copyConfigView(), sd.getElementById('config-copy-btn'));
        this.onEvent('click', () => this.#setSchemaViewMode('summary'), sd.getElementById('schema-view-summary-btn'));
        this.onEvent('click', () => this.#setSchemaViewMode('effective-json'), sd.getElementById('schema-view-effective-json-btn'));
        this.onEvent('click', () => this.#setSchemaViewMode('original-json'), sd.getElementById('schema-view-original-json-btn'));
        this.onEvent('click', () => this.#copySchemaJson('effective'), sd.getElementById('schema-copy-effective-btn'));
        this.onEvent('click', () => this.#copySchemaJson('original'), sd.getElementById('schema-copy-original-btn'));

        // Step navigation — tab clicks
        sd.querySelectorAll('.step-tab').forEach(tab => {
            this.onEvent('click', () => {
                if (!tab.disabled) this.#navigateToStep(tab.dataset.step);
            }, tab);
        });

        // Step navigation — next/prev buttons
        // Default flow: source → fields → visualize → customize → output
        // Time and Transform steps are accessible via the step nav tabs but skipped in the default flow
        this.onEvent('click', () => this.#navigateToStep('fields'), sd.getElementById('step-next-source'));
        this.onEvent('click', () => this.#navigateToStep('source'), sd.getElementById('step-prev-fields'));
        this.onEvent('click', () => this.#navigateToStep('visualize'), sd.getElementById('step-next-fields'));
        this.onEvent('click', () => this.#navigateToStep('fields'), sd.getElementById('step-prev-time'));
        this.onEvent('click', () => this.#navigateToStep('visualize'), sd.getElementById('step-next-time'));
        this.onEvent('click', () => this.#navigateToStep('fields'), sd.getElementById('step-prev-transform'));
        this.onEvent('click', () => this.#navigateToStep('visualize'), sd.getElementById('step-next-transform'));
        this.onEvent('click', () => this.#navigateToStep('fields'), sd.getElementById('step-prev-visualize'));
        this.onEvent('click', () => this.#navigateToStep('customize'), sd.getElementById('step-next-visualize'));
        this.onEvent('click', () => this.#navigateToStep('visualize'), sd.getElementById('step-prev-customize'));
        this.onEvent('click', () => this.#navigateToStep('output'), sd.getElementById('step-next-customize'));
        this.onEvent('click', () => this.#navigateToStep('customize'), sd.getElementById('step-prev-output'));

        // Fields
        this.onEvent('click', () => this.#setFieldsMode(false), sd.getElementById('fields-mode-auto-btn'));
        this.onEvent('click', () => this.#setFieldsMode(true), sd.getElementById('fields-mode-all-btn'));
        this.onEvent('click', () => this.#setAllFields(true),  sd.getElementById('fields-all-btn'));
        this.onEvent('click', () => this.#setAllFields(false), sd.getElementById('fields-none-btn'));
        const fieldSearch = sd.getElementById('field-search');
        this.onEvent('input', () => this.#filterFieldList(fieldSearch.value), fieldSearch);

        // Filters
        this.onEvent('click', () => this.#addFilter(), sd.getElementById('add-filter-btn'));
        this.onEvent('click', () => this.#clearFilters(), sd.getElementById('clear-filters-btn'));

        // Aggregation
        const aggrOp = sd.getElementById('aggr-op');
        this.onEvent('change', () => {
            this.#aggrOp = aggrOp.value;
            this.#updateAggrFieldVisibility();
            this.#syncControlsLauncherState();
        }, aggrOp);
        const aggrField = sd.getElementById('aggr-field');
        this.onEvent('change', () => {
            this.#aggrField = aggrField.value;
            this.#syncControlsLauncherState();
        }, aggrField);

        // GroupBy
        this.onEvent('click', () => this.#addGroupBy(), sd.getElementById('add-groupby-btn'));

        // Time range and granularity
        const granularitySelect = sd.getElementById('granularity-select');
        this.onEvent('change', () => {
            this.#granularity = granularitySelect.value;
            this.#constrainTimePickerByGranularity();
            this.#syncControlsLauncherState();
        }, granularitySelect);
        const startTime = sd.getElementById('start-time');
        this.onEvent('change', () => {
            this.#constrainTimePickerByGranularity();
            this.#clearTimePresetSelection();
            this.#syncControlsLauncherState();
        }, startTime);
        const endTime = sd.getElementById('end-time');
        this.onEvent('change', () => {
            this.#clearTimePresetSelection();
            this.#syncControlsLauncherState();
        }, endTime);

        // Time presets (recency shortcuts)
        sd.querySelectorAll('.time-preset-btn').forEach(btn => {
            this.onEvent('click', () => this.#applyTimePreset(btn), btn);
        });

        // Time scope buttons (rollup/journal)
        sd.querySelectorAll('.time-scope-btn').forEach(btn => {
            this.onEvent('click', () => this.#applyTimeScopePreset(btn), btn);
        });

        // Adornments
        this.onEvent('click', () => this.#addAdornment(), sd.getElementById('add-adornment-btn'));

        // Visualization type
        sd.querySelectorAll('.viz-type-btn').forEach(btn => {
            this.onEvent('click', () => this.#setVizType(btn.dataset.viz), btn);
        });

        // Chart axis mapping — live preview updates
        this.onEvent('change', () => this.#renderChartPreview(), sd.getElementById('viz-x-axis'));
        this.onEvent('change', () => this.#renderChartPreview(), sd.getElementById('viz-y-axis'));

        // Metric field mapping — live preview updates
        this.onEvent('change', () => this.#renderMetricPreview(), sd.getElementById('viz-metric-field'));
        this.onEvent('input', () => this.#renderMetricPreview(), sd.getElementById('viz-metric-title'));
        this.onEvent('input', () => this.#renderMetricPreview(), sd.getElementById('viz-metric-suffix'));

        // Column config reset
        this.onEvent('click', () => {
            this.#columnConfigs = [];
            this.#syncCustomizeSections();
        }, sd.getElementById('reset-col-config-btn'));

        // Output — copy config + timeScope toggle
        this.onEvent('click', () => this.#copyOutputConfig(), sd.getElementById('copy-config-btn'));
        this.onEvent('change', () => this.#generateOutputConfig(), sd.getElementById('output-timescope-toggle'));
        this.onEvent('change', () => this.#generateOutputConfig(), sd.getElementById('output-timescopes'));

        // Provider/org picker modal
        this.onEvent('click', () => this.#closeValuePicker(), sd.getElementById('provider-picker-cancel'));
        this.onEvent('click', () => this.#closeValuePicker(), sd.querySelector('.provider-picker-backdrop'));
        this.onEvent('click', () => this.#applyValuePickerSelection(), sd.getElementById('provider-picker-apply'));

        // Adorn field picker modal
        this.onEvent('click', () => this.#closeAdornFieldPicker(), sd.getElementById('adorn-field-picker-cancel'));
        this.onEvent('click', () => this.#closeAdornFieldPicker(), sd.querySelector('#adorn-field-picker-modal .provider-picker-backdrop'));
        this.onEvent('click', () => this.#applyAdornFieldPickerSelection(), sd.getElementById('adorn-field-picker-apply'));

        // Adorn key mapping modal
        this.onEvent('click', () => this.#closeAdornKeyMappingModal(), sd.getElementById('adorn-key-mapping-close'));
        this.onEvent('click', () => this.#closeAdornKeyMappingModal(), sd.getElementById('adorn-key-mapping-cancel'));
        this.onEvent('click', () => this.#closeAdornKeyMappingModal(), sd.querySelector('#adorn-key-mapping-modal .provider-picker-backdrop'));
        this.onEvent('click', () => this.#applyAdornKeyMapping(), sd.getElementById('adorn-key-mapping-apply'));
    }

    #captureControlsSectionSlots() {
        const parent = this.shadowRoot.getElementById('explorer-sidebar');
        if (!parent) {
            return;
        }
        const sectionIds = [ 'fields-section', 'filters-section', 'aggregation-section', 'adornment-section' ];
        this.#controlsSectionSlots.clear();
        for (const id of sectionIds) {
            const section = this.shadowRoot.getElementById(id);
            if (!section) {
                continue;
            }
            this.#controlsSectionSlots.set(id, { parent, nextSibling: section.nextElementSibling });
        }
    }

    #restoreControlsSection(sectionId = '') {
        const id = String(sectionId || '').trim();
        const section = this.shadowRoot.getElementById(id);
        const slot = this.#controlsSectionSlots.get(id);
        if (!section || !slot?.parent) {
            return;
        }
        if (slot.nextSibling && slot.nextSibling.parentElement === slot.parent) {
            slot.parent.insertBefore(section, slot.nextSibling);
        } else {
            slot.parent.appendChild(section);
        }
    }

    #syncControlsLauncherState() {
        this.#syncStepNav();
    }

    #syncStepNav() {
        const sd = this.shadowRoot;
        const hasSource = !!this.#selectedSource;

        // Enable/disable step tabs based on progress
        const tabs = sd.querySelectorAll('.step-tab');
        const steps = HSExplorer.STEPS;
        tabs.forEach(tab => {
            const step = tab.dataset.step;
            const idx = steps.indexOf(step);
            // source is always enabled; others need a source selected
            if (idx === 0) {
                tab.disabled = false;
            } else {
                tab.disabled = !hasSource;
            }
            // Mark active
            tab.classList.toggle('active', step === this.#currentStep);
            // Mark completed (all steps before current are considered completed)
            const currentIdx = steps.indexOf(this.#currentStep);
            tab.classList.toggle('completed', idx < currentIdx && !tab.disabled);
        });

        // Enable/disable next button on source step
        const nextSourceBtn = sd.getElementById('step-next-source');
        if (nextSourceBtn) {
            nextSourceBtn.disabled = !hasSource;
        }

        // Show/hide time-not-applicable hint and time sub-sections
        const timeField = hasSource ? this.#detectTimeField() : '';
        const isTimeSeries = hasSource ? this.#isTimeSeriesSource(this.#selectedSource) : false;
        const hasTimeSupport = !!timeField;
        const timeHint = sd.getElementById('time-not-applicable');
        const timeRecency = sd.getElementById('time-recency-section');
        const timeTimescope = sd.getElementById('time-timescope-section');
        const timeCustom = sd.getElementById('time-custom-section');
        if (timeHint) timeHint.classList.toggle('hidden', hasTimeSupport || !hasSource);
        if (timeRecency) timeRecency.classList.toggle('hidden', isTimeSeries || !hasTimeSupport);
        if (timeTimescope) timeTimescope.classList.toggle('hidden', !isTimeSeries);
        if (timeCustom) timeCustom.classList.toggle('hidden', !hasTimeSupport);

        // Disable the time step tab entirely when no time field is available
        const timeTab = sd.querySelector('.step-tab[data-step="time"]');
        if (timeTab && hasSource) {
            timeTab.disabled = !hasTimeSupport;
            timeTab.title = hasTimeSupport ? '' : 'No time field available for this source';
        }
    }

    #navigateToStep(step) {
        const sd = this.shadowRoot;
        const steps = HSExplorer.STEPS;
        if (!steps.includes(step)) return;

        // Hide current step panel
        const currentPanel = sd.getElementById(`step-${this.#currentStep}`);
        if (currentPanel) currentPanel.classList.add('hidden');

        // Show target step panel
        this.#currentStep = step;
        const targetPanel = sd.getElementById(`step-${step}`);
        if (targetPanel) targetPanel.classList.remove('hidden');

        // Update the instruction banner
        const instructionEl = sd.getElementById('step-instruction');
        if (instructionEl) instructionEl.textContent = HSExplorer.STEP_INSTRUCTIONS[step] || '';

        // Update the visualization sections visibility when entering visualize step
        if (step === 'visualize') {
            this.#syncVizSections();
            this.#syncPreviewVisibility();
        }

        // Restore table preview when going back to earlier steps
        if (steps.indexOf(step) < steps.indexOf('visualize') && this.#resultsView === 'card') {
            this.#restoreTablePreview();
        }

        // Generate output when entering output step
        if (step === 'output') {
            this.#syncOutputTimeScopeSection();
            this.#generateOutputConfig();
            // Collapse the left panel so results get full width
            const panels = sd.querySelector('.step-panels');
            if (panels) panels.classList.add('collapsed');
        } else {
            // Restore the left panel when navigating to any other step
            const panels = sd.querySelector('.step-panels');
            if (panels) panels.classList.remove('collapsed');
        }

        // Update customize sections when entering customize step
        if (step === 'customize') {
            this.#syncCustomizeSections();
        }

        this.#syncStepNav();
    }

    #syncSourceStepStatus() {
        const sd = this.shadowRoot;
        const nextBtn = sd.getElementById('step-next-source');
        const hasSource = !!this.#selectedSource;
        const hasData = this.#results.length > 0;

        if (nextBtn) {
            nextBtn.disabled = !hasSource;
            if (hasSource && !hasData) {
                nextBtn.textContent = 'No data — Next: Fields \u2192';
            } else {
                nextBtn.innerHTML = 'Next: Fields &rarr;';
            }
        }

        // Update the active source breadcrumb
        const crumb = sd.getElementById('active-source-crumb');
        if (crumb) {
            if (hasSource) {
                const name = this.#selectedSource.path?.replace(/^api\//, '') || '';
                const rowInfo = hasData ? `${this.#results.length} rows` : 'no data';
                crumb.textContent = `${name} (${rowInfo})`;
                crumb.classList.remove('hidden');
                crumb.classList.toggle('source-crumb-empty', !hasData);
            } else {
                crumb.textContent = '';
                crumb.classList.add('hidden');
            }
        }
    }

    #openControlsModal(sectionId = '', title = 'Explorer controls') {
        const section = this.shadowRoot.getElementById(sectionId);
        const modal = this.shadowRoot.getElementById('controls-modal');
        const titleEl = this.shadowRoot.getElementById('controls-modal-title');
        const content = this.shadowRoot.getElementById('controls-modal-content');
        if (!section || !modal || !content) {
            return;
        }

        if (section.classList.contains('hidden')) {
            this.#setStatus(`${title} controls are not available for this source`);
            return;
        }

        if (this.#activeControlsSectionId && this.#activeControlsSectionId !== sectionId) {
            this.#restoreControlsSection(this.#activeControlsSectionId);
        }

        this.#activeControlsSectionId = sectionId;
        if (titleEl) {
            titleEl.textContent = title;
        }
        content.innerHTML = '';
        content.appendChild(section);
        modal.classList.remove('hidden');
    }

    #closeControlsModal() {
        const modal = this.shadowRoot.getElementById('controls-modal');
        if (!modal) {
            return;
        }
        if (this.#activeControlsSectionId) {
            this.#restoreControlsSection(this.#activeControlsSectionId);
        }
        this.#activeControlsSectionId = '';
        modal.classList.add('hidden');
    }

    #initGrid() {
        requestAnimationFrame(() => {
            const target = this.shadowRoot.getElementById('table-target');
            if (!target) return;
            this.#dataView = new SlickDataView();
            this.#grid = new SlickGrid(target, this.#dataView, [], {
                shadowRoot: this.shadowRoot,
                enableCellNavigation: false,
                enableTextSelectionOnCells: true,
                // Avoid Sortable dependency requirement in this lightweight explorer grid.
                enableColumnReorder: false,
                // Preserve readable defaults and allow horizontal scrolling for wide datasets.
                forceFitColumns: false,
                multiColumnSort: true,
                enableAutoSizeColumns: false,
                rowHeight: 32,
            });
            this.#dataView.onRowCountChanged.subscribe((_, args) => {
                this.#setResultsCount(args.current);
                this.#grid.updateRowCount();
                this.#grid.render();
            });
            this.#dataView.onRowsChanged.subscribe((_, args) => {
                this.#grid.invalidateRows(args.rows);
                this.#grid.render();
            });
            const alphaComparer = new Intl.Collator(undefined, {});
            this.#grid.onSort.subscribe((_, args) => {
                this.#dataView.sort((a, b) => {
                    for (const { sortCol, sortAsc } of args.sortCols) {
                        const field = sortCol.field;
                        const sign = sortAsc ? 1 : -1;
                        const v1 = a[field], v2 = b[field];
                        if ((v1 == null) && v2 != null) return sign;
                        if ((v2 == null) && v1 != null) return -sign;
                        if (v1 !== v2) {
                            if (typeof v1 === 'string' && typeof v2 === 'string') return alphaComparer.compare(v1, v2) * sign;
                            return (v1 > v2 ? 1 : -1) * sign;
                        }
                    }
                    return 0;
                }, args.sortAsc);
                this.#grid.invalidate();
                this.#grid.render();
            });

            this.#grid.onHeaderClick.subscribe((_, args) => {
                const field = args?.column?.field || args?.column?.id || '';
                this.#activeHeaderField = field;
                this.#activeHeaderIndex = this.#getColumnIndexByField(field);
                this.#syncHeaderKeyboardState();
                this.#hideAdornShortcutMenu();
            });

            this.#grid.onHeaderContextMenu.subscribe((e, args) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                const field = args?.column?.field || args?.column?.id || '';
                this.#activeHeaderField = field;
                this.#activeHeaderIndex = this.#getColumnIndexByField(field);
                this.#syncHeaderKeyboardState();
                const x = e?.pageX ?? e?.clientX ?? 0;
                const y = e?.pageY ?? e?.clientY ?? 0;
                this.#showAdornShortcutMenu(field, x, y);
            });

            this.#syncHeaderKeyboardState();
        });
    }

    // ── Source loading & picking ────────────────────────────────────────────

    async #loadSources() {
        this.#setStatus('Loading sources...');
        try {
            const [sources, hiddenList] = await Promise.all([
                this.fetch('/api/sources'),
                this.fetch('/api/hidden-sources').catch(() => []),
            ]);
            this.#sources = sources;
            this.#hiddenSources = new Set(Array.isArray(hiddenList) ? hiddenList : []);
            // Determine if current user can manage hidden sources
            const usertype = UserSession.get('userinfo')?.usertype;
            this.#canManageHiddenSources = ['devops', 'developer', 'super'].includes(usertype);
            this.#setStatus('Select a data source to begin');
        } catch(err) {
            Logger.error("Explorer: failed to load sources: ", err);
            this.#setStatus('Failed to load sources — check connectivity');
        }
    }

    async #fetchSourceCapabilities(source) {
        if (!source) {
            return null;
        }
        if (source.field_rows) {
            return source;
        }
        try {
            const schemaPath = source.schema_path || (source.path + '?schema');
            const caps = await this.fetch('/' + String(schemaPath).replace(/^\//, ''));
            Object.assign(source, caps || {});
            return source;
        } catch (err) {
            Logger.warn('Explorer failed to load adorn source capabilities: ', source?.id, err?.message || err);
            return source;
        }
    }

    #selectInterestingFields(fieldRows = [], exclude = new Set(), maxCount = 12) {
        const rows = (fieldRows || []).filter(row => row?.id && !exclude.has(row.id));
        const chosen = [];
        const seen = new Set();

        const push = (fieldId) => {
            if (!fieldId || seen.has(fieldId) || exclude.has(fieldId)) {
                return;
            }
            seen.add(fieldId);
            chosen.push(fieldId);
        };

        // For adorn defaults, only auto-select key fields from the adorn source metadata.
        for (const row of rows) {
            if (row?.key || row?.kind === 'key') {
                push(row.id);
            }
            if (chosen.length >= maxCount) {
                break;
            }
        }

        return chosen;
    }

    // ── Source Picker (via hs-list-picker) ─────────────────────────────────

    #getSourcePickerItems(excludeIds = new Set()) {
        return this.#sources.filter(s => {
            if (excludeIds.has(s.id)) return false;
            if (!this.#showHiddenSources && this.#hiddenSources.has(s.id)) return false;
            return true;
        });
    }

    #buildSourcePickerTabs(excludeIds = new Set()) {
        const allItems = this.#getSourcePickerItems(excludeIds);
        const CAT_TYPES = HSExplorer.SOURCE_CATEGORY_TYPES;
        const tabs = [
            { id: 'all', label: 'All', items: allItems },
        ];
        for (const [cat, typeSet] of Object.entries(CAT_TYPES)) {
            const catItems = allItems.filter(s => typeSet.has(s.type) || s.id?.startsWith(cat + '.'));
            if (catItems.length > 0) {
                const label = cat.charAt(0).toUpperCase() + cat.slice(1);
                tabs.push({ id: cat, label, items: catItems });
            }
        }
        // Shared config for all tabs
        const shared = {
            getSearchText: s => [s.path, s.name, s.description, s.type],
            renderItem: s => ({
                title: (s.path || s.name || '').replace(/^api\//, ''),
                subtitle: s.description || '',
                badges: [s.type, s.format, s.is_compound ? 'compound' : ''].filter(Boolean),
            }),
            getValue: s => s,
            minTokenLength: 4,
            emptyText: 'No data sources match',
        };
        return tabs.map(t => ({ ...shared, ...t }));
    }

    #openSourcePickerModal({ title = 'Choose data source', excludeIds = [], onSelect } = {}) {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }

        const excludeSet = new Set(excludeIds);
        picker.open({
            title,
            tabs: this.#buildSourcePickerTabs(excludeSet),
            onSelect: (source) => {
                if (onSelect) onSelect(source);
            },
        });
    }

    #openSourcePickerForPrimary() {
        this.#openSourcePickerModal({
            title: 'Choose primary data source',
            onSelect: (source) => this.#selectSource(source),
        });
    }

    #openSourcePickerForAdornment(adornmentId) {
        const adorning = this.#adornments.find(a => a.id === adornmentId);
        if (!adorning) return;

        this.#openSourcePickerModal({
            title: 'Choose adornment source',
            excludeIds: [this.#selectedSource?.id].filter(Boolean),
            onSelect: (source) => {
                adorning.sourceId = source.id;
                adorning.keyMapping = [];
                adorning.fields.clear();
                this.#renderAdornmentList();
            },
        });
    }

    #updateSourcePickerTrigger(source) {
        const trigger = this.shadowRoot.getElementById('source-picker-trigger');
        const text = this.shadowRoot.getElementById('source-picker-trigger-text');
        if (!trigger || !text) return;

        if (source) {
            text.textContent = source.path.replace(/^api\//, '');
            trigger.classList.add('has-source');
            trigger.title = source.path;
        } else {
            text.textContent = 'Choose data source…';
            trigger.classList.remove('has-source');
            trigger.title = '';
        }
    }

    static SOURCE_CATEGORY_TYPES = {
        rollup: new Set(['rollup', 'device_rollup', 'user_rollup', 'stats', 'cassandra_stats']),
        telemetry: new Set(['telemetry']),
        cassandra: new Set(['cassandra_journal']),
        postgres: new Set(['postgres_journal']),
        keycloak: new Set(['keycloak']),
    };

    async #toggleSourceHidden(sourceId) {
        if (this.#hiddenSources.has(sourceId)) {
            this.#hiddenSources.delete(sourceId);
        } else {
            this.#hiddenSources.add(sourceId);
        }
        // Persist to server
        try {
            await this.fetch('/api/hidden-sources', {
                method: 'PUT',
                payload: Array.from(this.#hiddenSources),
            });
        } catch (err) {
            Logger.error('Explorer: failed to persist hidden sources:', err);
        }
    }


    #isRollupSource(source) {
        const type = String(source?.type || '').toLowerCase();
        const format = String(source?.format || '').toLowerCase();
        const path = String(source?.path || '').toLowerCase();
        const name = String(source?.name || '').toLowerCase();
        return type === 'device_rollup' || type === 'user_rollup' || format === 'rollup' || path.includes('/rollup/') || name.includes('rollup/');
    }

    #isJournalSource(source) {
        const type = String(source?.type || '').toLowerCase();
        const format = String(source?.format || '').toLowerCase();
        const path = String(source?.path || '').toLowerCase();
        const name = String(source?.name || '').toLowerCase();
        return type.includes('journal') || format.includes('journal') || path.includes('/journal/') || name.includes('journal');
    }

    #isTimeSeriesSource(source) {
        return this.#isRollupSource(source) || this.#isJournalSource(source);
    }

    #detectTimeField() {
        // Returns the name of the usable time field for the current source, or '' if none
        const fieldRows = this.#schema?.field_rows || [];
        const fields = this.#schema?.fields || [];
        const knownTimeFields = ['utc_timestamp_inms', 'timestamp', 'ts'];

        // Check field_rows for explicit time-typed fields
        for (const row of fieldRows) {
            const id = String(row?.id || '').trim();
            const type = String(row?.type || '').toLowerCase();
            const format = String(row?.format || '').toLowerCase();
            if (type === 'timestamp' || type === 'datetime' || format === 'date-time') {
                return id;
            }
        }

        // Check for known time field names in the schema
        const allFieldIds = fieldRows.length > 0
            ? new Set(fieldRows.map(r => r?.id).filter(Boolean))
            : new Set(fields.map(f => f?.id || f?.field || f || '').filter(Boolean));

        for (const name of knownTimeFields) {
            if (allFieldIds.has(name)) return name;
        }

        // Time-series sources always have time support even without an explicit field
        if (this.#isTimeSeriesSource(this.#selectedSource)) {
            return 'utc_timestamp_inms';
        }

        return '';
    }

    #getRequiredFilterFields() {
        const querySchema = this.#selectedSource?.query_schema || {};
        const required = [].concat(querySchema.required_filter_fields || []).map(x => String(x || '').trim()).filter(Boolean);
        const requiredAny = [].concat(querySchema.required_any_filter_fields || []).map(x => String(x || '').trim()).filter(Boolean);
        return { required, requiredAny };
    }

    #getFieldMetaMap() {
        const map = new Map();
        const rows = this.#schema?.field_rows || [];
        for (const row of rows) {
            if (row?.id) {
                map.set(row.id, row);
            }
        }
        return map;
    }

    #getSchemaKeyFieldIds() {
        const keyFields = new Set();

        const sourceKeys = []
            .concat(this.#selectedSource?.keys || [])
            .concat(this.#selectedSource?.cache_primary_key || [])
            .concat(this.#selectedSource?.discovery?.rollup_keys || []);
        for (const key of sourceKeys) {
            const normalized = String(key || '').trim();
            if (normalized) {
                keyFields.add(normalized);
            }
        }

        const rows = this.#schema?.field_rows || [];
        for (const row of rows) {
            const field = String(row?.id || '').trim();
            if (!field) {
                continue;
            }
            if (row?.key || row?.kind === 'key') {
                keyFields.add(field);
            }
        }

        return keyFields;
    }

    #resolveRollupScopeField(schemaFields, keyFields) {
        const sourceType = String(this.#selectedSource?.type || '').toLowerCase();
        const preferredByType = {
            device_rollup: [ 'deviceuuid', 'useruuid', 'providerid' ],
            user_rollup: [ 'useruuid', 'deviceuuid', 'providerid' ],
            rollup: [ 'providerid', 'deviceuuid', 'useruuid' ],
        };

        const preferred = preferredByType[sourceType] || [ 'providerid', 'deviceuuid', 'useruuid' ];
        for (const field of preferred) {
            if (keyFields.has(field)) {
                return field;
            }
        }

        // Fallback for sparse metadata where key markers are unavailable.
        for (const field of preferred) {
            if (schemaFields.has(field)) {
                return field;
            }
        }

        return '';
    }

    #getSessionProviderIds() {
        const sessionLists = [
            UserSession.get('selected_providers') || [],
            UserSession.get('filtered_providers') || [],
            UserSession.get('providers') || [],
        ];
        const ids = [];
        for (const list of sessionLists) {
            for (const item of list) {
                const id = item?.providerid || item?.id || '';
                if (id) {
                    ids.push(String(id));
                }
            }
            if (ids.length > 0) {
                break;
            }
        }
        return ids;
    }

    #getSessionOrgIds() {
        const sessionLists = [
            UserSession.get('selected_orgs') || [],
            UserSession.get('filtered_orgs') || [],
            UserSession.get('orgs') || [],
        ];
        const ids = [];
        for (const list of sessionLists) {
            for (const item of list) {
                const id = item?.orgid || item?.id || '';
                if (id) {
                    ids.push(String(id));
                }
            }
            if (ids.length > 0) {
                break;
            }
        }
        return ids;
    }

    #buildScopeFilterAst() {
        const schemaFields = new Set((this.#schema?.field_rows || []).map(x => x?.id).filter(Boolean));
        const keyFields = this.#getSchemaKeyFieldIds();
        const scopeParts = [];
        const isRollup = this.#isRollupSource(this.#selectedSource);

        if (isRollup) {
            const identityField = this.#resolveRollupScopeField(schemaFields, keyFields);
            if (identityField === 'providerid') {
                const providers = this.#getSessionProviderIds();
                if (providers.length === 1) {
                    scopeParts.push([ 'providerid', '==', providers[0] ]);
                } else if (providers.length > 1) {
                    scopeParts.push([ 'providerid', 'in', providers ]);
                }
            }
        } else if (schemaFields.has('providerid')) {
            const providers = this.#getSessionProviderIds();
            if (providers.length === 1) {
                scopeParts.push([ 'providerid', '==', providers[0] ]);
            } else if (providers.length > 1) {
                scopeParts.push([ 'providerid', 'in', providers ]);
            }
        }

        if (schemaFields.has('orgid')) {
            const orgs = this.#getSessionOrgIds();
            if (orgs.length === 1) {
                scopeParts.push([ 'orgid', '==', orgs[0] ]);
            } else if (orgs.length > 1) {
                scopeParts.push([ 'orgid', 'in', orgs ]);
            }
        }

        if (scopeParts.length < 1) {
            return null;
        }
        if (scopeParts.length === 1) {
            return scopeParts[0];
        }

        const expr = [];
        scopeParts.forEach((part, i) => {
            expr.push(part);
            if (i < scopeParts.length - 1) expr.push('and');
        });
        return expr;
    }

    #getDefaultDayValue() {
        const startVal = this.shadowRoot.getElementById('start-time')?.value;
        if (!startVal) {
            return '';
        }
        const start = new Date(startVal);
        if (Number.isNaN(start.getTime())) {
            return '';
        }
        return start.toISOString().slice(0, 10);
    }

    #parseEnumValues(fieldMeta) {
        const raw = String(fieldMeta?.enum || '').trim();
        if (!raw) {
            return [];
        }
        return raw.split(',').map(x => x.trim()).filter(Boolean);
    }

    async #probeFieldValues(fieldIds = []) {
        const fields = Array.from(new Set(fieldIds.map(x => String(x || '').trim()).filter(Boolean)));
        if (!this.#selectedSource || fields.length < 1) {
            return new Map();
        }

        const params = new URLSearchParams();
        params.set('rid', Math.random().toString(36).slice(2));
        params.set('fields', fields.join(','));
        params.set('limit', '120');

        const startVal = this.shadowRoot.getElementById('start-time').value;
        const endVal = this.shadowRoot.getElementById('end-time').value;
        if (startVal) params.set('starttime', String(new Date(startVal).getTime()));
        if (endVal) params.set('endtime', String(new Date(endVal).getTime()));

        const url = `/${this.#selectedSource.path}?${params.toString()}`;
        try {
            const json = await this.fetch(url);
            const rows = Array.isArray(json) ? json : (json ? [json] : []);
            const byField = new Map();
            for (const fieldId of fields) {
                const values = [];
                const seen = new Set();
                for (const row of rows) {
                    const value = row?.[fieldId];
                    if (value === undefined || value === null || value === '') {
                        continue;
                    }
                    const key = typeof value === 'object' ? JSON.stringify(value) : String(value);
                    if (seen.has(key)) {
                        continue;
                    }
                    seen.add(key);
                    values.push(value);
                    if (values.length >= 8) {
                        break;
                    }
                }
                byField.set(fieldId, values);
            }
            return byField;
        } catch (err) {
            Logger.debug('Explorer preview probe failed, continuing with heuristic defaults: ', err?.message || err);
            return new Map();
        }
    }

    #pickDefaultForField(fieldId, fieldMeta, probeValues = new Map()) {
        const id = String(fieldId || '').trim();
        const lower = id.toLowerCase();

        const probed = probeValues.get(id) || [];
        if (probed.length > 0) {
            return probed[0];
        }

        if (lower === 'providerid' || lower.endsWith('_providerid')) {
            const providers = this.#getSessionProviderIds();
            if (providers.length > 0) {
                return providers[0];
            }
        }

        if (lower === 'day') {
            const day = this.#getDefaultDayValue();
            if (day) {
                return day;
            }
        }

        const enumValues = this.#parseEnumValues(fieldMeta);
        if (enumValues.length > 0) {
            return enumValues[0];
        }

        const type = String(fieldMeta?.type || '').toLowerCase();
        if (type === 'boolean' || type === 'bool') {
            return true;
        }

        return '';
    }

    async #applyAutoPreviewDefaults() {
        const { required, requiredAny } = this.#getRequiredFilterFields();
        this.#autoPreviewDefaults = [];
        if (required.length < 1 && requiredAny.length < 1) {
            this.#renderAutoPreviewDefaults();
            return;
        }

        const applied = [];
        const existing = new Set(this.#filters.map(f => f.field));
        const fieldMetaMap = this.#getFieldMetaMap();

        const unresolvedFields = [];
        for (const field of required) {
            if (!existing.has(field)) {
                unresolvedFields.push(field);
            }
        }

        const hasRequiredAny = requiredAny.some(field => existing.has(field));
        if (!hasRequiredAny && requiredAny.length > 0) {
            unresolvedFields.push(...requiredAny.filter(field => !existing.has(field)));
        }

        let probeValues = new Map();
        if (this.#isRollupSource(this.#selectedSource) && unresolvedFields.length > 0) {
            probeValues = await this.#probeFieldValues(unresolvedFields);
        }

        for (const field of required) {
            if (existing.has(field)) {
                continue;
            }
            const value = this.#pickDefaultForField(field, fieldMetaMap.get(field), probeValues);
            if (value === '' || value === undefined || value === null) {
                continue;
            }
            this.#filters.push({ id: ++this.#filterIdCounter, field, op: '==', value: String(value) });
            applied.push({ filterId: this.#filterIdCounter, field, op: '==', value: String(value), reason: 'required' });
            existing.add(field);
        }

        const requiredAnySatisfied = requiredAny.some(field => existing.has(field));
        if (!requiredAnySatisfied) {
            for (const field of requiredAny) {
                const value = this.#pickDefaultForField(field, fieldMetaMap.get(field), probeValues);
                if (value === '' || value === undefined || value === null) {
                    continue;
                }
                this.#filters.push({ id: ++this.#filterIdCounter, field, op: '==', value: String(value) });
                applied.push({ filterId: this.#filterIdCounter, field, op: '==', value: String(value), reason: 'required-any' });
                existing.add(field);
                break;
            }
        }

        this.#autoPreviewDefaults = applied;
        this.#renderAutoPreviewDefaults();
        this.#renderFilterList();
    }

    #setResultsView(view = 'card') {
        const valid = ['card', 'json', 'schema', 'config'];
        const nextView = valid.includes(view) ? view : 'card';
        this.#resultsView = nextView;

        const sd = this.shadowRoot;
        const cardBtn = sd.getElementById('results-view-card-btn');
        const jsonBtn = sd.getElementById('results-view-json-btn');
        const schemaBtn = sd.getElementById('results-view-schema-btn');
        const configBtn = sd.getElementById('results-view-config-btn');
        const tableTarget = sd.getElementById('table-target');
        const chartTarget = sd.getElementById('chart-preview-target');
        const metricTarget = sd.getElementById('metric-preview-target');
        const jsonTarget = sd.getElementById('json-target');
        const schemaTarget = sd.getElementById('schema-target');
        const configTarget = sd.getElementById('config-target');

        const isCard = nextView === 'card';
        const isJson = nextView === 'json';
        const isSchema = nextView === 'schema';
        const isConfig = nextView === 'config';

        // Toggle button active states
        if (cardBtn) { cardBtn.classList.toggle('active', isCard); cardBtn.setAttribute('aria-pressed', String(isCard)); }
        if (jsonBtn) { jsonBtn.classList.toggle('active', isJson); jsonBtn.setAttribute('aria-pressed', String(isJson)); }
        if (schemaBtn) { schemaBtn.classList.toggle('active', isSchema); schemaBtn.setAttribute('aria-pressed', String(isSchema)); }
        if (configBtn) { configBtn.classList.toggle('active', isConfig); configBtn.setAttribute('aria-pressed', String(isConfig)); }

        // Show/hide data panes based on viz type + view toggle
        const isChart = this.#vizType === 'line' || this.#vizType === 'bar';
        const isMetric = this.#vizType === 'metric';
        const isVizTable = this.#vizType === 'table';

        if (tableTarget) tableTarget.classList.toggle('hidden', !(isCard && isVizTable));
        if (chartTarget) chartTarget.classList.toggle('hidden', !(isCard && isChart));
        if (metricTarget) metricTarget.classList.toggle('hidden', !(isCard && isMetric));
        if (jsonTarget) jsonTarget.classList.toggle('hidden', !isJson);
        if (schemaTarget) schemaTarget.classList.toggle('hidden', !isSchema);
        if (configTarget) configTarget.classList.toggle('hidden', !isConfig);

        if (isCard && isVizTable) {
            this.#grid?.resizeCanvas?.();
            this.#grid?.invalidate?.();
            this.#syncHeaderKeyboardState();
        } else if (isCard && isChart) {
            this.#renderChartPreview();
        } else if (isCard && isMetric) {
            this.#renderMetricPreview();
        } else if (isJson) {
            this.#renderJsonView(this.#results);
        } else if (isSchema) {
            this.#renderSchemaView();
        } else if (isConfig) {
            this.#renderConfigView();
        }
        this.#syncCopyJsonButton();
    }

    #syncCopyJsonButton() {
        const btn = this.shadowRoot.getElementById('results-copy-json-btn');
        if (!btn) {
            return;
        }
        const hasRows = Array.isArray(this.#results) && this.#results.length > 0;
        const show = this.#resultsView === 'json';
        btn.classList.toggle('hidden', !show);
        btn.disabled = !hasRows;
        btn.title = hasRows
            ? 'Copy current JSON rows to clipboard'
            : 'No rows to copy';
    }

    #safeClone(value) {
        try {
            return JSON.parse(JSON.stringify(value));
        } catch (_) {
            return null;
        }
    }

    async #copyTextToClipboard(text = '') {
        const payload = String(text || '');
        if (!payload) {
            return false;
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(payload);
            } else {
                const el = document.createElement('textarea');
                el.value = payload;
                el.setAttribute('readonly', 'readonly');
                el.style.position = 'fixed';
                el.style.opacity = '0';
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
            return true;
        } catch (_) {
            return false;
        }
    }

    async #copyJsonView() {
        const rows = Array.isArray(this.#results) ? this.#results : [];
        if (rows.length < 1) {
            this.#setStatus('No rows to copy');
            return;
        }

        const payload = JSON.stringify(rows, null, 2);
        try {
            const copied = await this.#copyTextToClipboard(payload);
            if (!copied) {
                throw new Error('clipboard unavailable');
            }
            this.#setStatus(`Copied ${rows.length} row${rows.length === 1 ? '' : 's'} JSON to clipboard`);
        } catch (err) {
            Logger.warn('Explorer: failed to copy JSON payload: ', err?.message || err);
            this.#setStatus('Copy failed — clipboard unavailable');
        }
    }

    #schemaModePayload(mode = 'summary') {
        if (mode === 'original-json') {
            return this.#sourceSchemaOriginal;
        }
        return this.#schema;
    }

    #setSchemaViewMode(mode = 'summary') {
        const next = [ 'summary', 'effective-json', 'original-json' ].includes(mode) ? mode : 'summary';
        this.#schemaViewMode = next;
        this.#renderSchemaView();
    }

    #renderSyntaxJson(target, payload) {
        if (!target) {
            return;
        }
        const pretty = JSON.stringify(payload ?? null, null, 2);
        const tokenRegex = /"(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"\s*:|"(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

        let html = '';
        let cursor = 0;
        let match = tokenRegex.exec(pretty);
        while (match) {
            const token = match[0];
            html += SlickGridUtils.escapeHtml(pretty.slice(cursor, match.index));
            html += `<span class="${this.#jsonTokenClass(token)}">${SlickGridUtils.escapeHtml(token)}</span>`;
            cursor = match.index + token.length;
            match = tokenRegex.exec(pretty);
        }
        html += SlickGridUtils.escapeHtml(pretty.slice(cursor));
        target.innerHTML = `<pre class="json-pretty">${html}</pre>`;
    }

    #schemaList(values = []) {
        const list = Array.isArray(values) ? values : [];
        if (list.length < 1) {
            return '<span class="schema-pill">none</span>';
        }
        return list.slice(0, 80).map(value => `<span class="schema-pill">${SlickGridUtils.escapeHtml(String(value))}</span>`).join('');
    }

    #schemaFlag(value) {
        if (value === true) {
            return 'yes';
        }
        if (value === false) {
            return 'no';
        }
        return '';
    }

    #schemaWhenText(row = {}) {
        if (row?.when && typeof row.when === 'string' && row.when.trim()) {
            return row.when;
        }
        if (row?.when_conditions && typeof row.when_conditions === 'object') {
            return JSON.stringify(row.when_conditions);
        }
        return '';
    }

    #renderSchemaSummary(payload) {
        const summary = this.shadowRoot.getElementById('schema-summary-content');
        if (!summary) {
            return;
        }

        const schemaDoc = payload || {};
        const fieldRows = Array.isArray(schemaDoc?.field_rows) ? schemaDoc.field_rows : [];
        const filtering = schemaDoc?.filtering || {};
        const discovery = schemaDoc?.discovery || {};
        const operatorsMeta = (discovery?.operators && typeof discovery.operators === 'object') ? discovery.operators : {};
        const operatorNames = Object.keys(operatorsMeta);
        const expectedDimensions = (discovery?.expected_dimensions && typeof discovery.expected_dimensions === 'object')
            ? discovery.expected_dimensions
            : {};

        const operatorCards = operatorNames.length < 1
            ? '<div class="schema-meta-item">No source-specific rollup/stats operators metadata present.</div>'
            : operatorNames.map(name => {
                const meta = operatorsMeta[name] || {};
                const valueMode = String(meta?.value_mode || '');
                const outputFields = Array.isArray(meta?.output_fields) ? meta.output_fields : [];
                const configuredFields = Array.isArray(meta?.configured_fields) ? meta.configured_fields : [];
                return `
                    <div class="schema-op-card">
                      <div class="op-title">${SlickGridUtils.escapeHtml(name)}</div>
                      <div><span class="k">value_mode:</span>${SlickGridUtils.escapeHtml(valueMode || '-')}</div>
                      <div><span class="k">configured_fields:</span>${SlickGridUtils.escapeHtml(configuredFields.join(', ') || '-')}</div>
                      <div><span class="k">output_fields:</span>${SlickGridUtils.escapeHtml(outputFields.join(', ') || '-')}</div>
                    </div>
                `;
            }).join('');

        const operatorHints = this.#schemaList(OPERATORS.map(o => o.value));
        const rowsHtml = fieldRows.length < 1
            ? '<tr><td colspan="10">No field metadata available.</td></tr>'
            : fieldRows.map(row => {
                const enumValues = Array.isArray(row?.enum_values)
                    ? row.enum_values
                    : String(row?.enum || '').split(',').map(x => x.trim()).filter(Boolean);
                return `
                    <tr>
                      <td>${SlickGridUtils.escapeHtml(String(row?.id || ''))}</td>
                      <td>${SlickGridUtils.escapeHtml(String(row?.type || ''))}</td>
                      <td>${SlickGridUtils.escapeHtml(String(row?.kind || ''))}</td>
                      <td>${SlickGridUtils.escapeHtml(String(row?.source || ''))}</td>
                      <td>${SlickGridUtils.escapeHtml(this.#schemaFlag(row?.key))}</td>
                      <td>${SlickGridUtils.escapeHtml(this.#schemaFlag(row?.filterable))}</td>
                      <td>${SlickGridUtils.escapeHtml(this.#schemaFlag(row?.groupable))}</td>
                      <td>${SlickGridUtils.escapeHtml(this.#schemaFlag(row?.aggregatable))}</td>
                      <td>${SlickGridUtils.escapeHtml(enumValues.join(', '))}</td>
                      <td>${SlickGridUtils.escapeHtml(this.#schemaWhenText(row))}</td>
                    </tr>
                `;
            }).join('');

        summary.innerHTML = `
            <section class="schema-section">
              <h4 class="schema-section-title">Schema Overview</h4>
              <div class="schema-meta-grid">
                <div class="schema-meta-item"><span class="k">source:</span>${SlickGridUtils.escapeHtml(String(schemaDoc?.name || this.#selectedSource?.name || ''))}</div>
                <div class="schema-meta-item"><span class="k">path:</span>${SlickGridUtils.escapeHtml(String(schemaDoc?.path || this.#selectedSource?.path || ''))}</div>
                <div class="schema-meta-item"><span class="k">type:</span>${SlickGridUtils.escapeHtml(String(schemaDoc?.type || this.#selectedSource?.type || ''))}</div>
                <div class="schema-meta-item"><span class="k">format:</span>${SlickGridUtils.escapeHtml(String(schemaDoc?.format || this.#selectedSource?.format || ''))}</div>
                <div class="schema-meta-item"><span class="k">fields:</span>${SlickGridUtils.escapeHtml(String(fieldRows.length || 0))}</div>
                <div class="schema-meta-item"><span class="k">keys:</span>${SlickGridUtils.escapeHtml([].concat(schemaDoc?.keys || []).join(', ') || '-')}</div>
              </div>
            </section>

            <section class="schema-section">
              <h4 class="schema-section-title">Filter Policy and Operators</h4>
              <div class="schema-meta-grid">
                <div class="schema-meta-item"><span class="k">required_filter_fields:</span>${SlickGridUtils.escapeHtml([].concat(filtering?.required_filter_fields || []).join(', ') || '-')}</div>
                <div class="schema-meta-item"><span class="k">required_any_filter_fields:</span>${SlickGridUtils.escapeHtml([].concat(filtering?.required_any_filter_fields || []).join(', ') || '-')}</div>
                <div class="schema-meta-item"><span class="k">restricted_filter_fields:</span>${SlickGridUtils.escapeHtml([].concat(filtering?.restricted_filter_fields || []).join(', ') || '-')}</div>
                <div class="schema-meta-item"><span class="k">denied_filter_fields:</span>${SlickGridUtils.escapeHtml([].concat(filtering?.denied_filter_fields || []).join(', ') || '-')}</div>
              </div>
              <div class="schema-pill-list">${operatorHints}</div>
            </section>

            <section class="schema-section">
              <h4 class="schema-section-title">Rollup or Stats Operators</h4>
              <div class="schema-op-grid">${operatorCards}</div>
              <div class="schema-meta-item" style="margin-top:8px;"><span class="k">expected_dimensions:</span>${SlickGridUtils.escapeHtml(Object.keys(expectedDimensions).join(', ') || '-')}</div>
            </section>

            <section class="schema-section">
              <h4 class="schema-section-title">Field Catalog</h4>
              <div class="schema-fields-table-wrap">
                <table class="schema-fields-table">
                  <thead>
                    <tr>
                      <th>field</th>
                      <th>type</th>
                      <th>kind</th>
                      <th>source</th>
                      <th>key</th>
                      <th>filterable</th>
                      <th>groupable</th>
                      <th>aggregatable</th>
                      <th>enum values</th>
                      <th>when</th>
                    </tr>
                  </thead>
                  <tbody>${rowsHtml}</tbody>
                </table>
              </div>
            </section>
        `;
    }

    #renderSchemaView() {
        const summary = this.shadowRoot.getElementById('schema-summary-content');
        const json = this.shadowRoot.getElementById('schema-json-content');
        const summaryBtn = this.shadowRoot.getElementById('schema-view-summary-btn');
        const effectiveBtn = this.shadowRoot.getElementById('schema-view-effective-json-btn');
        const originalBtn = this.shadowRoot.getElementById('schema-view-original-json-btn');
        const copyEffectiveBtn = this.shadowRoot.getElementById('schema-copy-effective-btn');
        const copyOriginalBtn = this.shadowRoot.getElementById('schema-copy-original-btn');
        if (!summary || !json) {
            return;
        }

        const hasEffective = !!this.#schema;
        const hasOriginal = !!this.#sourceSchemaOriginal;
        const mode = this.#schemaViewMode;
        const showSummary = mode === 'summary';
        const showEffectiveJson = mode === 'effective-json';
        const showOriginalJson = mode === 'original-json';

        summary.classList.toggle('hidden', !showSummary);
        json.classList.toggle('hidden', showSummary);

        if (summaryBtn) {
            summaryBtn.classList.toggle('active', showSummary);
            summaryBtn.setAttribute('aria-pressed', showSummary ? 'true' : 'false');
        }
        if (effectiveBtn) {
            effectiveBtn.classList.toggle('active', showEffectiveJson);
            effectiveBtn.setAttribute('aria-pressed', showEffectiveJson ? 'true' : 'false');
            effectiveBtn.disabled = !hasEffective;
        }
        if (originalBtn) {
            originalBtn.classList.toggle('active', showOriginalJson);
            originalBtn.setAttribute('aria-pressed', showOriginalJson ? 'true' : 'false');
            originalBtn.disabled = !hasOriginal;
        }
        if (copyEffectiveBtn) {
            copyEffectiveBtn.disabled = !hasEffective;
        }
        if (copyOriginalBtn) {
            copyOriginalBtn.disabled = !hasOriginal;
        }

        if (showSummary) {
            this.#renderSchemaSummary(this.#schema || this.#sourceSchemaOriginal || {});
            return;
        }

        if (showEffectiveJson) {
            this.#renderSyntaxJson(json, this.#schema || {});
            return;
        }

        this.#renderSyntaxJson(json, this.#sourceSchemaOriginal || {});
    }

    async #copySchemaJson(kind = 'effective') {
        const isOriginal = kind === 'original';
        const payload = isOriginal ? this.#sourceSchemaOriginal : this.#schema;
        if (!payload) {
            this.#setStatus(isOriginal ? 'Original schema payload is unavailable' : 'Effective schema payload is unavailable');
            return;
        }

        const label = isOriginal ? 'original schema' : 'effective schema';
        try {
            const copied = await this.#copyTextToClipboard(JSON.stringify(payload, null, 2));
            if (!copied) {
                throw new Error('clipboard unavailable');
            }
            this.#setStatus(`Copied ${label} JSON to clipboard`);
        } catch (err) {
            Logger.warn('Explorer: failed to copy schema payload: ', err?.message || err);
            this.#setStatus('Copy failed — clipboard unavailable');
        }
    }

    #jsonTokenClass(token = '') {
        if (/^".*":\s*$/.test(token)) {
            return 'json-key';
        }
        if (/^".*"$/.test(token)) {
            return 'json-string';
        }
        if (/^(true|false)$/.test(token)) {
            return 'json-boolean';
        }
        if (/^null$/.test(token)) {
            return 'json-null';
        }
        return 'json-number';
    }

    #renderJsonView(rows = []) {
        const target = this.shadowRoot.getElementById('json-target');
        if (!target) {
            return;
        }
        this.#renderSyntaxJson(target, Array.isArray(rows) ? rows : []);
    }

    async #selectSource(source) {
        if (this.#fieldSelectionRefreshTimer) {
            clearTimeout(this.#fieldSelectionRefreshTimer);
            this.#fieldSelectionRefreshTimer = null;
        }

        this.#closeControlsModal();

        this.#selectedSource = source;
        this.#selectedFields.clear();
        this.#hasUserFieldSelection = false;
        this.#filters = [];
        this.#adornments = [];
        this.#adornmentIdCounter = 0;
        this.#aggrOp = '';
        this.#aggrField = '';
        this.#schema = null;
        this.#sourceSchemaOriginal = null;
        this.#results = [];
        this.#granularity = '';
        this.#autoPreviewDefaults = [];
        this.#fieldValueOptionsCache.clear();
        this.#showAllFields = true;
        this.#autoVisibleFieldIds = new Set();
        this.#schemaViewMode = 'summary';
        this.#updateFieldsModeButtons();
        this.#renderAutoPreviewDefaults();

        // Clear previous source table immediately so stale columns/rows are not shown.
        if (this.#dataView) {
            this.#dataView.setItems([]);
        }
        if (this.#grid) {
            this.#grid.setColumns([]);
            this.#grid.invalidate();
            this.#grid.render();
            this.#syncHeaderKeyboardState();
        }
        this.#setResultsCount(0);
        this.#renderJsonView([]);

        // Update the source picker trigger button
        this.#updateSourcePickerTrigger(source);
        this.#syncControlsLauncherState();
        this.#showSourceLoading('Loading schema\u2026');

        // Time-series detection for step visibility hints
        const isTimeSeries = this.#isTimeSeriesSource(source);

        // Show granularity selector for rollups
        const isRollup = this.#isRollupSource(source);
        const granularityCtrl = this.shadowRoot.getElementById('granularity-control');
        if (granularityCtrl) granularityCtrl.classList.toggle('hidden', !isRollup);

        if (isTimeSeries) {
            // Use a conservative default preview window to keep initial source load snappy.
            this.#setDefaultTimeRange({ hours: 24 });
            if (isRollup) {
                this.#detectGranularity(source.name);
                this.#constrainTimePickerByGranularity();
            }
            this.#renderAdornmentList();
        } else {
            // Non-time-series sources should not carry previous time filters.
            this.shadowRoot.getElementById('start-time').value = '';
            this.shadowRoot.getElementById('end-time').value = '';
        }

        // Reset aggregation
        this.#resetAggregation();
        this.#renderFilterList();

        // Load schema
        this.#setStatus('Loading schema…');
        try {
            const schemaPath = source.schema_path || (source.path + '?schema');
            const caps = await this.fetch('/' + schemaPath.replace(/^\//, ''));
            this.#schema = caps;
            this.#sourceSchemaOriginal = this.#safeClone(caps);
            const fieldRows = caps.field_rows || [];
            this.#populateAggrFieldSelect(fieldRows);
            this.#setStatus(`${fieldRows.length} fields — loading preview…`);
        } catch(err) {
            Logger.error("Explorer: failed to load schema: ", err);
            this.#renderFieldList([]);
            this.#sourceSchemaOriginal = null;
            this.#setStatus('Schema unavailable — loading preview with auto-detected columns…');
        }

        this.#renderSchemaView();

        await this.#applyAutoPreviewDefaults();
        this.#autoSelectDefaultFields(this.#schema?.field_rows || []);
        this.#ensureDefaultFieldSelection(this.#schema?.field_rows || []);
        await this.#refreshAutoVisibleFields(this.#schema?.field_rows || []);
        this.#renderFieldList(this.#schema?.field_rows || []);

        // Immediately run a preview query once a source is selected.
        this.#showSourceLoading('Loading preview\u2026');
        let previewResult;
        if (isTimeSeries) {
            previewResult = await this.#runProgressiveAutoPreview();
        } else {
            previewResult = await this.#runQuery({ isAutoPreview: true });
        }
        this.#hideSourceLoading();

        // Update source step UI based on result
        this.#syncSourceStepStatus();

        // Only auto-advance to Fields step if we got data.
        // If source returned no rows, stay on Source step so user can easily pick another.
        if (this.#results.length > 0) {
            this.#navigateToStep('fields');
        }
    }

    // ── Time range and granularity ──────────────────────────────────────────

    #detectGranularity(sourceName) {
        // Parse granularity from source name, e.g. "rollup/stats/user/accounts/monthly"
        const parts = sourceName.split('/');
        const lastPart = parts[parts.length - 1];
        if (['hourly', 'daily', 'monthly', 'quarterly'].includes(lastPart)) {
            this.#granularity = lastPart;
            this.shadowRoot.getElementById('granularity-select').value = lastPart;
        }
    }

    #setDefaultTimeRange({ hours = 24 } = {}) {
        const now = new Date();
        const endTime = new Date(now);
        endTime.setSeconds(0); endTime.setMilliseconds(0);

        const startTime = new Date(now);
        startTime.setHours(startTime.getHours() - hours);
        startTime.setSeconds(0); startTime.setMilliseconds(0);

        // Convert to datetime-local format (YYYY-MM-DDTHH:mm)
        const startVal = startTime.toISOString().slice(0, 16);
        const endVal = endTime.toISOString().slice(0, 16);

        this.shadowRoot.getElementById('start-time').value = startVal;
        this.shadowRoot.getElementById('end-time').value = endVal;
        this.#syncControlsLauncherState();
    }

    #applyTimePreset(btn) {
        const hours = parseInt(btn.dataset.hours, 10);
        const sd = this.shadowRoot;
        // Update active state
        sd.querySelectorAll('.time-preset-btn').forEach(b => b.classList.toggle('active', b === btn));
        if (hours === 0) {
            // All time — clear the date range
            sd.getElementById('start-time').value = '';
            sd.getElementById('end-time').value = '';
            this.#syncControlsLauncherState();
        } else {
            this.#setDefaultTimeRange({ hours });
        }
    }

    #applyTimeScopePreset(btn) {
        const scope = btn.dataset.scope;
        const sd = this.shadowRoot;
        sd.querySelectorAll('.time-scope-btn').forEach(b => b.classList.toggle('active', b === btn));
        // Map scope to hours for the time range
        const scopeHours = { day: 24, week: 24 * 7, month: 24 * 30, quarter: 24 * 90 };
        const hours = scopeHours[scope] || 24 * 7;
        this.#setDefaultTimeRange({ hours });
        if (this.#granularity) {
            this.#constrainTimePickerByGranularity();
        }
    }

    #clearTimePresetSelection() {
        // When user manually edits the date picker, deselect any active preset
        const sd = this.shadowRoot;
        sd.querySelectorAll('.time-preset-btn.active').forEach(b => b.classList.remove('active'));
        sd.querySelectorAll('.time-scope-btn.active').forEach(b => b.classList.remove('active'));
    }

    #formatAutoWindowLabel(hours) {
        if (hours < 48) {
            return '1 day';
        }
        const days = Math.round(hours / 24);
        return `${days} days`;
    }

    async #runProgressiveAutoPreview() {
        const windows = [ 24, 24 * 7, 24 * 30 ];
        let last = null;

        for (let i = 0; i < windows.length; i += 1) {
            const hours = windows[i];
            this.#setDefaultTimeRange({ hours });
            if (this.#isRollupSource(this.#selectedSource) && this.#granularity) {
                this.#constrainTimePickerByGranularity();
            }

            const result = await this.#runQuery({ isAutoPreview: true });
            last = result;
            if (!result?.ok) {
                return result;
            }
            if (result.rows > 0) {
                if (i > 0) {
                    this.#setStatus(`Preview loaded — ${result.rows} row${result.rows === 1 ? '' : 's'} (auto-expanded to last ${this.#formatAutoWindowLabel(hours)})`);
                }
                return result;
            }
        }

        if (last?.ok && last.rows === 0) {
            this.#setStatus('Preview loaded — 0 rows returned (auto-tried last 1 day, 7 days, and 30 days)');
        }
        return last;
    }

    #constrainTimePickerByGranularity() {
        if (!this.#granularity) return;

        const startEl = this.shadowRoot.getElementById('start-time');
        const endEl = this.shadowRoot.getElementById('end-time');

        // Parse current values
        const startStr = startEl.value;
        const endStr = endEl.value;

        if (!startStr || !endStr) return;

        const start = new Date(startStr);
        const end = new Date(endStr);

        // Adjust start to align with granularity boundary
        const constrainedStart = this.#constrainToGranularityBoundary(start, this.#granularity, 'floor');
        const constrainedEnd = this.#constrainToGranularityBoundary(end, this.#granularity, 'ceil');

        // Only update if changed
        const newStartVal = constrainedStart.toISOString().slice(0, 16);
        const newEndVal = constrainedEnd.toISOString().slice(0, 16);

        if (startStr !== newStartVal) startEl.value = newStartVal;
        if (endStr !== newEndVal) endEl.value = newEndVal;
    }

    #constrainToGranularityBoundary(date, granularity, direction = 'floor') {
        const d = new Date(date);
        if (granularity === 'hourly') {
            if (direction === 'floor') { d.setMinutes(0); }
            else { d.setMinutes(59); d.setSeconds(59); }
        } else if (granularity === 'daily') {
            if (direction === 'floor') { d.setHours(0, 0, 0); }
            else { d.setHours(23, 59, 59); }
        } else if (granularity === 'monthly') {
            if (direction === 'floor') {
                d.setDate(1);
                d.setHours(0, 0, 0);
            } else {
                d.setMonth(d.getMonth() + 1);
                d.setDate(0);
                d.setHours(23, 59, 59);
            }
        } else if (granularity === 'quarterly') {
            const quarter = Math.floor(d.getMonth() / 3);
            if (direction === 'floor') {
                d.setMonth(quarter * 3);
                d.setDate(1);
                d.setHours(0, 0, 0);
            } else {
                d.setMonth((quarter + 1) * 3);
                d.setDate(0);
                d.setHours(23, 59, 59);
            }
        }
        d.setMilliseconds(0);
        return d;
    }

    // ── Adornments ─────────────────────────────────────────────────────────

    #addAdornment() {
        this.#adornments.push({ id: ++this.#adornmentIdCounter, sourceId: '', keyMapping: [], fields: new Set() });
        this.#renderAdornmentList();
    }

    #removeAdornment(adornmentId) {
        const adornment = this.#adornments.find(a => a.id === adornmentId);
        if (adornment && this.#schema?.field_rows) {
            // Remove adornment fields from schema
            const sourceTag = `adorn:${adornment.sourceId}`;
            this.#schema.field_rows = this.#schema.field_rows.filter(f => f.source !== sourceTag);
            // Remove adornment fields from selected
            for (const field of (adornment.fields || new Set())) {
                this.#selectedFields.delete(field);
            }
        }
        this.#adornments = this.#adornments.filter(a => a.id !== adornmentId);
        this.#renderAdornmentList();
        this.#renderFieldList(this.#schema?.field_rows || []);
        this.#renderSchemaView();
        
        // Trigger query refresh
        this.#refreshGridColumnsFromSelection();
    }

    async #openAdornFieldPicker(adornmentId) {
        const adornment = this.#adornments.find(a => a.id === adornmentId);
        if (!adornment?.sourceId) {
            return;
        }
        const source = this.#sources.find(s => s.id === adornment.sourceId);
        const sourceCaps = await this.#fetchSourceCapabilities(source);
        const fieldRows = sourceCaps?.field_rows || [];
        if (fieldRows.length < 1) {
            this.#setStatus(`No field metadata available for ${source?.path || source?.id || 'adorn source'}`);
            return;
        }

        const modal = this.shadowRoot.getElementById('adorn-field-picker-modal');
        const title = this.shadowRoot.getElementById('adorn-field-picker-title');
        const select = this.shadowRoot.getElementById('adorn-field-picker-select');
        if (!modal || !title || !select) {
            return;
        }

        const exclude = new Set((adornment.keyMapping || []).map(m => m.adornKey).filter(Boolean));
        const defaults = adornment.fields?.size > 0
            ? Array.from(adornment.fields)
            : this.#selectInterestingFields(fieldRows, exclude, 12);
        const selected = new Set(defaults);

        title.textContent = `Choose fields from ${source?.path?.replace(/^api\//, '') || source?.id || 'source'}`;
        select.innerHTML = '';
        for (const row of fieldRows) {
            if (!row?.id || exclude.has(row.id)) {
                continue;
            }
            const option = document.createElement('option');
            option.value = row.id;
            option.textContent = `${row.name || row.id}${row.type ? ` (${row.type})` : ''}`;
            if (selected.has(row.id)) {
                option.selected = true;
            }
            select.appendChild(option);
        }

        this.#adornFieldPickerAdornmentId = adornmentId;
        modal.classList.remove('hidden');
        select.focus();
    }

    #closeAdornFieldPicker() {
        const modal = this.shadowRoot.getElementById('adorn-field-picker-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
        this.#adornFieldPickerAdornmentId = null;
    }

    #applyAdornFieldPickerSelection() {
        if (!this.#adornFieldPickerAdornmentId) {
            this.#closeAdornFieldPicker();
            return;
        }
        const adornment = this.#adornments.find(a => a.id === this.#adornFieldPickerAdornmentId);
        const select = this.shadowRoot.getElementById('adorn-field-picker-select');
        if (!adornment || !select) {
            this.#closeAdornFieldPicker();
            return;
        }

        const selected = Array.from(select.selectedOptions || []).map(option => option.value).filter(Boolean);
        adornment.fields = new Set(selected);
        
        // Inject adornment fields into schema so they appear in field list and query
        this.#injectAdornmentFieldsIntoSchema();
        
        this.#renderAdornmentList();
        this.#closeAdornFieldPicker();
        
        // Show adornments section and refresh field list
        const adornSection = this.shadowRoot.getElementById('adornment-section');
        if (adornSection) {
            adornSection.classList.remove('hidden');
        }
        this.#renderFieldList(this.#schema?.field_rows || []);
        this.#renderSchemaView();
        
        // Prepend ALL adornment fields to selected fields so they appear on the LEFT in the table
        const allAdornFields = new Set();
        for (const a of this.#adornments) {
            for (const f of a.fields) allAdornFields.add(f);
        }
        const nonAdornFields = Array.from(this.#selectedFields).filter(f => !allAdornFields.has(f));
        this.#selectedFields.clear();
        for (const field of allAdornFields) {
            this.#selectedFields.add(field);
        }
        for (const field of nonAdornFields) {
            this.#selectedFields.add(field);
        }
        
        // Immediately run query to show adornment results (no debounce)
        this.#runQuery({ isAutoPreview: true });
    }

    #injectAdornmentFieldsIntoSchema() {
        if (!this.#schema?.field_rows) {
            return;
        }
        
        // Remove old adornment fields from schema
        this.#schema.field_rows = this.#schema.field_rows.filter(f => !f.source?.startsWith('adorn:'));
        
        // Add current adornment fields to schema with source metadata
        for (const adornment of this.#adornments) {
            if (!adornment.sourceId || adornment.fields.size === 0) {
                continue;
            }
            const adornSource = this.#sources.find(s => s.id === adornment.sourceId);
            if (!adornSource || !adornSource.field_rows) {
                // Defer fetching if not cached yet
                this.#fetchSourceCapabilities(adornSource).then(caps => {
                    if (caps?.field_rows) {
                        this.#injectAdornmentFieldsIntoSchema();
                        this.#renderFieldList(this.#schema?.field_rows || []);
                    }
                }).catch(err => {
                    Logger.debug('Failed to fetch adornment source capabilities: ', err?.message || err);
                });
                continue;
            }
            
            const sourceTag = `adorn:${adornment.sourceId}`;
            // Add only the selected adornment fields to schema
            for (const fieldRow of adornSource.field_rows) {
                if (adornment.fields.has(fieldRow.id)) {
                    const copy = { ...fieldRow, source: sourceTag };
                    // Check if already exists (avoid duplicates)
                    const exists = this.#schema.field_rows.some(f => f.id === copy.id && f.source === sourceTag);
                    if (!exists) {
                        this.#schema.field_rows.push(copy);
                    }
                }
            }
        }
        this.#renderSchemaView();
    }

    #renderAdornmentList() {
        const adornmentList = this.shadowRoot.getElementById('adornment-list');
        adornmentList.innerHTML = '';

        for (let i = 0; i < this.#adornments.length; i++) {
            const adorning = this.#adornments[i];
            const row = document.createElement('div');
            row.className = 'adornment-row';

            // Order number
            const orderNum = document.createElement('span');
            orderNum.className = 'adornment-order';
            orderNum.textContent = `${i + 1}.`;

            // Source button — opens source picker modal
            const sourceBtn = document.createElement('button');
            sourceBtn.type = 'button';
            sourceBtn.className = 'adornment-source-btn';
            const adornSource = this.#sources.find(s => s.id === adorning.sourceId);
            if (adornSource) {
                sourceBtn.textContent = adornSource.path.replace(/^api\//, '');
                sourceBtn.classList.add('has-source');
                sourceBtn.title = adornSource.path;
            } else {
                sourceBtn.textContent = 'Choose source…';
                sourceBtn.title = 'Click to choose an adornment source';
            }
            sourceBtn.addEventListener('click', () => this.#openSourcePickerForAdornment(adorning.id));

            // Configure keys button — opens key mapping modal
            const configBtn = document.createElement('button');
            configBtn.type = 'button';
            configBtn.className = 'filter-pick-btn';
            const allKeysMapped = this.#isAdornmentKeyComplete(adorning);
            const primaryKeys = adornSource?.keys || [];
            if (!adorning.sourceId) {
                configBtn.textContent = 'Keys';
                configBtn.disabled = true;
            } else if (primaryKeys.length === 0) {
                configBtn.textContent = 'No keys';
                configBtn.disabled = true;
                configBtn.title = 'This source has no primary key — cannot adorn';
            } else if (allKeysMapped) {
                configBtn.textContent = `\u2713 Keys (${primaryKeys.length})`;
                configBtn.style.color = '#28a745';
            } else {
                configBtn.textContent = `\u26A0 Keys (${primaryKeys.length})`;
                configBtn.style.color = '#c57600';
            }
            configBtn.addEventListener('click', () => this.#openAdornKeyMappingModal(adorning.id));

            // Fields button — only enabled when all keys are mapped
            const fieldBtn = document.createElement('button');
            fieldBtn.type = 'button';
            fieldBtn.className = 'filter-pick-btn';
            fieldBtn.textContent = 'Fields';
            fieldBtn.disabled = !adorning.sourceId || !allKeysMapped;
            if (!allKeysMapped && adorning.sourceId && primaryKeys.length > 0) {
                fieldBtn.title = 'Configure key mapping first';
            }
            fieldBtn.addEventListener('click', () => this.#openAdornFieldPicker(adorning.id));

            // Remove button
            const removeBtn = document.createElement('button');
            removeBtn.className = 'adornment-remove';
            removeBtn.textContent = '\u00d7';
            removeBtn.title = 'Remove this adornment';
            removeBtn.addEventListener('click', () => this.#removeAdornment(adorning.id));

            // Field count display
            const fieldDisplay = document.createElement('span');
            fieldDisplay.className = 'adornment-fields-display';
            fieldDisplay.title = `Fields: ${Array.from(adorning.fields).join(', ') || 'none selected'}`;
            const fieldCount = adorning.fields.size;
            fieldDisplay.textContent = fieldCount > 0 
                ? `${fieldCount} field${fieldCount === 1 ? '' : 's'}` 
                : '';
            fieldDisplay.style.fontSize = '0.85em';
            fieldDisplay.style.color = fieldCount > 0 ? '#666' : '#999';

            row.appendChild(orderNum);
            row.appendChild(sourceBtn);
            row.appendChild(configBtn);
            row.appendChild(fieldBtn);
            row.appendChild(fieldDisplay);
            row.appendChild(removeBtn);
            adornmentList.appendChild(row);
        }
        this.#syncControlsLauncherState();
        this.#renderJoinOnlyFields();
    }

    #isAdornmentKeyComplete(adornment) {
        if (!adornment?.keyMapping || adornment.keyMapping.length === 0) return false;
        return adornment.keyMapping.every(m => m.adornKey && m.primaryField);
    }

    #getAvailableFieldsForAdornment(adornmentIndex) {
        // Returns all fields available at this position in the adornment chain:
        // primary source fields + fields brought in by adornments 0..adornmentIndex-1
        const primaryFields = this.#getActiveFieldIds();
        const schemaKeyFields = Array.from(this.#getSchemaKeyFieldIds());
        const allSchemaFields = (this.#schema?.field_rows || []).map(r => r?.id).filter(Boolean);
        const available = new Set([...primaryFields, ...schemaKeyFields, ...allSchemaFields]);

        for (let i = 0; i < adornmentIndex && i < this.#adornments.length; i++) {
            const prior = this.#adornments[i];
            if (prior.fields) {
                for (const f of prior.fields) available.add(f);
            }
        }

        return [...available];
    }

    #openAdornKeyMappingModal(adornmentId) {
        const adornment = this.#adornments.find(a => a.id === adornmentId);
        if (!adornment?.sourceId) return;

        const adornSource = this.#sources.find(s => s.id === adornment.sourceId);
        const primaryKeys = (adornSource?.keys || []).filter(k => k != null);
        if (primaryKeys.length === 0) return;

        const adornIndex = this.#adornments.indexOf(adornment);
        const availableFields = this.#getAvailableFieldsForAdornment(adornIndex);

        // Initialize keyMapping if needed
        if (!adornment.keyMapping || adornment.keyMapping.length !== primaryKeys.length) {
            adornment.keyMapping = primaryKeys.map(k => ({
                adornKey: k,
                primaryField: availableFields.includes(k) ? k : '',
            }));
        }

        // Create a draft copy for modal editing
        this.#keyMappingDraft = adornment.keyMapping.map(m => ({ ...m }));
        this.#keyMappingAdornmentId = adornmentId;

        const sd = this.shadowRoot;
        const modal = sd.getElementById('adorn-key-mapping-modal');
        const titleEl = sd.getElementById('adorn-key-mapping-title');
        const rowsEl = sd.getElementById('adorn-key-mapping-rows');

        const sourceName = adornSource?.path?.replace(/^api\//, '') || adornSource?.id || 'source';
        titleEl.textContent = `Key Mapping: ${sourceName}`;
        rowsEl.innerHTML = '';

        for (const mapping of this.#keyMappingDraft) {
            const row = document.createElement('div');
            row.className = 'adorn-key-map-row';

            const adornSide = document.createElement('div');
            adornSide.className = 'adorn-key-map-adorn-side';
            adornSide.innerHTML = `${SlickGridUtils.escapeHtml(mapping.adornKey)} <span class="key-badge">KEY</span>`;

            const arrow = document.createElement('span');
            arrow.className = 'adorn-key-map-arrow';
            arrow.textContent = '\u2190';

            const primarySide = document.createElement('div');
            primarySide.className = 'adorn-key-map-primary-side';
            const sel = document.createElement('select');
            sel.innerHTML = `<option value="">— choose matching field —</option>` +
                availableFields.map(f =>
                    `<option value="${f}" ${f === mapping.primaryField ? 'selected' : ''}>${f}</option>`
                ).join('');
            sel.className = mapping.primaryField ? 'mapped' : 'unmapped';
            sel.addEventListener('change', () => {
                mapping.primaryField = sel.value;
                sel.className = sel.value ? 'mapped' : 'unmapped';
                this.#syncAdornKeyMappingApplyBtn();
            });
            primarySide.appendChild(sel);

            row.appendChild(adornSide);
            row.appendChild(arrow);
            row.appendChild(primarySide);
            rowsEl.appendChild(row);
        }

        this.#syncAdornKeyMappingApplyBtn();
        modal.classList.remove('hidden');
    }

    #syncAdornKeyMappingApplyBtn() {
        const btn = this.shadowRoot.getElementById('adorn-key-mapping-apply');
        if (!btn) return;
        const allMapped = this.#keyMappingDraft.every(m => m.adornKey && m.primaryField);
        btn.disabled = !allMapped;
        btn.title = allMapped ? '' : 'Map all key fields to enable apply';
    }

    #closeAdornKeyMappingModal() {
        const modal = this.shadowRoot.getElementById('adorn-key-mapping-modal');
        if (modal) modal.classList.add('hidden');
        this.#keyMappingAdornmentId = null;
        this.#keyMappingDraft = [];
    }

    #applyAdornKeyMapping() {
        const adornment = this.#adornments.find(a => a.id === this.#keyMappingAdornmentId);
        if (!adornment) {
            this.#closeAdornKeyMappingModal();
            return;
        }
        adornment.keyMapping = this.#keyMappingDraft.map(m => ({ ...m }));
        this.#closeAdornKeyMappingModal();
        this.#renderAdornmentList();

        // Auto-open field picker if keys are now complete and no fields selected yet
        if (this.#isAdornmentKeyComplete(adornment) && adornment.fields.size === 0) {
            this.#openAdornFieldPicker(adornment.id);
        }
    }

    // ── Join-only Field Pruning ─────────────────────────────────────────────

    #getJoinKeyFields() {
        // Collect all fields used as primary-side keys in adornment mappings
        const joinFields = new Set();
        for (const adornment of this.#adornments) {
            if (!adornment.keyMapping) continue;
            for (const m of adornment.keyMapping) {
                if (m.primaryField) joinFields.add(m.primaryField);
            }
        }
        return joinFields;
    }

    #renderJoinOnlyFields() {
        const section = this.shadowRoot.getElementById('join-fields-section');
        const list = this.shadowRoot.getElementById('join-fields-list');
        if (!section || !list) return;

        const joinKeyFields = this.#getJoinKeyFields();
        // Only show fields that are used for joining but not explicitly selected by the user
        const selectedFields = this.#selectedFields;
        const candidates = [...joinKeyFields].filter(f => {
            // Show if it's a join key — user can toggle to exclude from final output
            return f && joinKeyFields.has(f);
        });

        if (candidates.length === 0 || this.#adornments.length === 0) {
            section.classList.add('hidden');
            return;
        }

        section.classList.remove('hidden');
        list.innerHTML = '';

        for (const field of candidates) {
            const row = document.createElement('div');
            const isExcluded = this.#joinOnlyFields.has(field);
            row.className = `join-field-row${isExcluded ? ' excluded' : ''}`;

            const name = document.createElement('span');
            name.className = 'join-field-name';
            name.textContent = field;

            const reason = document.createElement('span');
            reason.className = 'join-field-reason';
            reason.textContent = isExcluded ? 'excluded from output' : 'included in output';

            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'join-field-toggle';
            toggle.textContent = isExcluded ? 'Include' : 'Exclude';
            toggle.addEventListener('click', () => {
                if (this.#joinOnlyFields.has(field)) {
                    this.#joinOnlyFields.delete(field);
                } else {
                    this.#joinOnlyFields.add(field);
                }
                this.#renderJoinOnlyFields();
            });

            row.appendChild(name);
            row.appendChild(reason);
            row.appendChild(toggle);
            list.appendChild(row);
        }
    }

    #getAdornmentKeyFields(adornment) {
        // Returns { key: [...primaryFields], source_key: [...adornKeys] } for query/config output
        const mapping = adornment?.keyMapping || [];
        const keys = mapping.map(m => m.primaryField).filter(Boolean);
        const sourceKeys = mapping.map(m => m.adornKey).filter(Boolean);
        return { key: keys, source_key: sourceKeys };
    }

    #normalizeSourceKeys(source = {}) {
        return [].concat(source?.keys || []).map(x => String(x || '').trim()).filter(Boolean);
    }

    #getAdornShortcutCandidates(field = '') {
        const key = String(field || '').trim();
        if (!key) {
            return [];
        }
        return this.#sources
            .filter(source => source?.id !== this.#selectedSource?.id)
            .filter(source => this.#normalizeSourceKeys(source).includes(key))
            .sort((a, b) => {
                const ap = String(a?.path || a?.name || a?.id || '');
                const bp = String(b?.path || b?.name || b?.id || '');
                return ap.localeCompare(bp);
            });
    }

    #hideAdornShortcutMenu() {
        const menu = this.shadowRoot.getElementById('adorn-shortcut-menu');
        if (!menu) {
            return;
        }
        menu.classList.add('hidden');
        menu.innerHTML = '';
    }

    async #applyAdornShortcut(sourceId, joinKey) {
        if (!sourceId || !joinKey) {
            return;
        }
        const adornSource = this.#sources.find(s => s.id === sourceId);
        const primaryKeys = adornSource?.keys || [];

        let adornment = this.#adornments.find(a => a.sourceId === sourceId);
        if (!adornment) {
            // Pre-initialize keyMapping with auto-matched fields where possible
            const adornIndex = this.#adornments.length;
            const availableFields = this.#getAvailableFieldsForAdornment(adornIndex);
            const keyMapping = primaryKeys.map(k => ({
                adornKey: k,
                primaryField: availableFields.includes(k) ? k : (k === joinKey ? joinKey : ''),
            }));
            adornment = { id: ++this.#adornmentIdCounter, sourceId, keyMapping, fields: new Set() };
            this.#adornments.push(adornment);
        }
        this.#renderAdornmentList();
        this.#hideAdornShortcutMenu();
        this.#setStatus(`Adornment added: ${primaryKeys.join(', ')} -> ${sourceId}`);

        // Open key mapping modal so user can confirm/adjust the mapping
        this.#openAdornKeyMappingModal(adornment.id);
    }

    #showAdornShortcutMenu(field = '', x = 0, y = 0) {
        const key = String(field || '').trim();
        if (!key) {
            return;
        }

        const menu = this.shadowRoot.getElementById('adorn-shortcut-menu');
        if (!menu) {
            return;
        }

        const candidates = this.#getAdornShortcutCandidates(key);
        menu.innerHTML = '';

        const title = document.createElement('div');
        title.className = 'adorn-shortcut-title';
        title.textContent = `Adorn by ${key}`;
        menu.appendChild(title);

        if (candidates.length < 1) {
            const empty = document.createElement('div');
            empty.className = 'adorn-shortcut-empty';
            empty.textContent = 'No compatible sources found where this field is a key.';
            menu.appendChild(empty);
        } else {
            for (const source of candidates) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'adorn-shortcut-item';

                const path = document.createElement('div');
                path.className = 'adorn-shortcut-item-path';
                path.textContent = String(source.path || source.name || source.id || '');

                const meta = document.createElement('div');
                meta.className = 'adorn-shortcut-item-meta';
                meta.textContent = `join key: ${key}`;

                btn.appendChild(path);
                btn.appendChild(meta);
                btn.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    this.#applyAdornShortcut(source.id, key);
                });
                menu.appendChild(btn);
            }
        }

        const margin = 10;
        const viewportW = window.innerWidth || 1280;
        const viewportH = window.innerHeight || 720;
        const estW = 420;
        const estH = 280;
        const left = Math.max(margin, Math.min(x, viewportW - estW - margin));
        const top = Math.max(margin, Math.min(y, viewportH - estH - margin));
        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;
        menu.classList.remove('hidden');
    }

    #getGridColumns() {
        return this.#grid?.getColumns?.() || [];
    }

    #getColumnIndexByField(field = '') {
        const target = String(field || '');
        if (!target) {
            return -1;
        }
        const cols = this.#getGridColumns();
        return cols.findIndex(col => (col?.field || col?.id || '') === target);
    }

    #getHeaderCells() {
        return Array.from(this.shadowRoot.querySelectorAll('#table-target .slick-header-column'));
    }

    #syncHeaderKeyboardState({ focus = false } = {}) {
        const cols = this.#getGridColumns();
        const cells = this.#getHeaderCells();
        if (cols.length < 1 || cells.length < 1) {
            return;
        }

        let index = this.#activeHeaderIndex;
        if (index < 0 || index >= cols.length) {
            index = this.#getColumnIndexByField(this.#activeHeaderField);
        }
        if (index < 0 || index >= cols.length) {
            index = 0;
        }

        this.#activeHeaderIndex = index;
        this.#activeHeaderField = cols[index]?.field || cols[index]?.id || '';

        const max = Math.min(cells.length, cols.length);
        for (let i = 0; i < max; i += 1) {
            const active = i === index;
            cells[i].setAttribute('tabindex', active ? '0' : '-1');
            cells[i].classList.toggle('explorer-header-active', active);
        }

        if (focus && cells[index]) {
            cells[index].focus({ preventScroll: true });
        }
    }

    #openAdornMenuForActiveHeader() {
        const cols = this.#getGridColumns();
        if (cols.length < 1) {
            return;
        }

        this.#syncHeaderKeyboardState();
        const index = this.#activeHeaderIndex >= 0 ? this.#activeHeaderIndex : 0;
        const field = this.#activeHeaderField || cols[index]?.field || cols[index]?.id || '';
        if (!field) {
            return;
        }

        const cells = this.#getHeaderCells();
        const cell = cells[index] || cells[0];
        if (cell) {
            const rect = cell.getBoundingClientRect();
            this.#showAdornShortcutMenu(field, rect.left + 12, rect.bottom + 8);
        } else {
            this.#showAdornShortcutMenu(field, 320, 120);
        }
    }

    #onGridHeaderKeydown(ev) {
        if (!this.#grid) {
            return;
        }
        const key = ev?.key;
        if (!key) {
            return;
        }

        if (key === 'ArrowLeft' || key === 'ArrowRight') {
            const cols = this.#getGridColumns();
            if (cols.length < 1) {
                return;
            }
            ev.preventDefault();
            this.#syncHeaderKeyboardState();
            const delta = key === 'ArrowLeft' ? -1 : 1;
            const next = Math.max(0, Math.min(cols.length - 1, this.#activeHeaderIndex + delta));
            this.#activeHeaderIndex = next;
            this.#activeHeaderField = cols[next]?.field || cols[next]?.id || '';
            this.#syncHeaderKeyboardState({ focus: true });
            this.#hideAdornShortcutMenu();
            return;
        }

        const openShortcut = key === 'Enter' || key === 'ContextMenu' || (key === 'F10' && ev.shiftKey);
        if (openShortcut) {
            ev.preventDefault();
            this.#openAdornMenuForActiveHeader();
        }
    }

    // ── Field list ──────────────────────────────────────────────────────────

    #deduplicateFieldRows(fieldRows) {
        // Build a set of all field ids for fast lookup
        const allIds = new Set(fieldRows.map(r => r.id));

        // Detect prefixed duplicates: if both "x" and "prefix__x" exist, suppress "x"
        const suppressedIds = new Set();
        for (const row of fieldRows) {
            const id = row.id;
            if (!id || id.includes('__')) continue;
            // Check if any prefixed variant exists
            for (const other of allIds) {
                if (other.endsWith('__' + id) && other !== id) {
                    suppressedIds.add(id);
                    break;
                }
            }
        }

        // Exclude complex nested types (object/array) that aren't usable as flat columns
        // Also exclude synthetic composite id fields (format like "providerid.useruuid")
        return fieldRows.filter(row => {
            if (suppressedIds.has(row.id)) return false;
            const type = String(row.type || '').toLowerCase();
            if (type === 'object' || type === 'array') return false;
            if (row.id === 'id' && row.format && row.format.includes('.')) return false;
            return true;
        });
    }

    #renderFieldList(fieldRows) {
        const fieldList = this.shadowRoot.getElementById('field-list');
        fieldList.innerHTML = '';

        if (fieldRows.length === 0) {
            fieldList.textContent = 'No field metadata available';
            this.#syncControlsLauncherState();
            return;
        }

        let rowsToRender = this.#deduplicateFieldRows(fieldRows);
        if (!this.#showAllFields && this.#autoVisibleFieldIds.size > 0) {
            rowsToRender = rowsToRender.filter(row => this.#autoVisibleFieldIds.has(row.id) || this.#selectedFields.has(row.id));
        }
        if (rowsToRender.length === 0) {
            rowsToRender = this.#deduplicateFieldRows(fieldRows);
        }

        // Group by source tag
        const groups = new Map();
        for (const row of rowsToRender) {
            const group = row.source || '';
            if (!groups.has(group)) groups.set(group, []);
            groups.get(group).push(row);
        }

        // Sort groups: keys first, base next, adorn:* alphabetically, ungrouped last
        const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
            const rank = (g) => g === 'key' ? 0 : g === 'base' ? 1 : g.startsWith('adorn:') ? 2 : 3;
            const ra = rank(a), rb = rank(b);
            if (ra !== rb) return ra - rb;
            return a.localeCompare(b);
        });

        for (const [group, rows] of sortedGroups) {
            const groupEl = document.createElement('div');
            groupEl.className = 'field-group';

            const labelEl = document.createElement('div');
            labelEl.className = 'field-group-label';
            labelEl.textContent = this.#groupLabel(group);
            groupEl.appendChild(labelEl);

            for (const row of rows) {
                const btn = document.createElement('button');
                const isRollupSelector = this.#isRollupConfiguredSelectorField(row);
                btn.type = 'button';
                btn.className = 'field-item' + (this.#selectedFields.has(row.id) ? ' selected' : '') + (isRollupSelector ? ' selector-only' : '');
                btn.dataset.fieldId = row.id;
                btn.dataset.fieldMode = isRollupSelector ? 'selector' : 'column';
                if (row.description) btn.title = row.description;

                const nameSpan = document.createElement('span');
                nameSpan.className = 'field-name' + (row.key ? ' is-key' : '');
                nameSpan.textContent = row.name || row.id;

                const typeSpan = document.createElement('span');
                typeSpan.className = 'field-type';
                typeSpan.textContent = isRollupSelector ? 'selector' : (row.type || '');

                btn.appendChild(nameSpan);
                btn.appendChild(typeSpan);

                btn.addEventListener('click', () => {
                    if (isRollupSelector) {
                        this.#applyRollupConfiguredSelector(row);
                        return;
                    }
                    this.#hasUserFieldSelection = true;
                    if (this.#selectedFields.has(row.id)) {
                        this.#selectedFields.delete(row.id);
                        btn.classList.remove('selected');
                    } else {
                        this.#selectedFields.add(row.id);
                        btn.classList.add('selected');
                    }
                    this.#refreshGridColumnsFromSelection();
                });

                groupEl.appendChild(btn);
            }
            fieldList.appendChild(groupEl);
        }
        this.#syncControlsLauncherState();
    }

    #groupLabel(group) {
        if (this.#isRollupSource(this.#selectedSource) && group === 'configured') return 'Configured Dimensions (click to filter by field)';
        if (!group || group === 'base') return 'Base Fields';
        if (group === 'key') return 'Key Fields';
        if (group.startsWith('adorn:')) return `From: ${group.slice(6)}`;
        return group;
    }

    #isRollupConfiguredSelectorField(row = null) {
        if (!this.#isRollupSource(this.#selectedSource)) {
            return false;
        }

        const id = String(row?.id || '').trim();
        if (!id) {
            return false;
        }

        if (id === 'field' || id === 'stats_type') {
            return false;
        }
        if (row?.key || row?.kind === 'key') {
            return false;
        }

        const sourceTag = String(row?.source || '').toLowerCase();
        const kind = String(row?.kind || '').toLowerCase();
        const hasWhen = !!row?.when || !!row?.when_conditions;
        return sourceTag === 'configured' && (kind === 'dimension' || hasWhen);
    }

    #upsertFilterField(field = '', op = '==', value = '', { onlyIfMissing = false } = {}) {
        const normalizedField = String(field || '').trim();
        if (!normalizedField) {
            return null;
        }
        const normalizedValue = String(value ?? '').trim();
        if (!normalizedValue) {
            return null;
        }

        const existing = this.#filters.find(f => String(f?.field || '').trim() === normalizedField);
        if (existing) {
            const existingValue = String(existing?.value ?? '').trim();
            if (!onlyIfMissing || !existingValue) {
                existing.op = op;
                existing.value = normalizedValue;
            }
            return existing;
        }

        const next = {
            id: ++this.#filterIdCounter,
            field: normalizedField,
            op,
            value: normalizedValue,
        };
        this.#filters.push(next);
        return next;
    }

    #applyRollupConfiguredSelector(row = null) {
        if (!this.#isRollupConfiguredSelectorField(row)) {
            return;
        }

        const selectorField = String(row?.id || '').trim();
        if (!selectorField) {
            return;
        }

        this.#upsertFilterField('field', '==', selectorField);

        const statsTypes = [].concat(row?.when_conditions?.stats_type || [])
            .map(x => String(x || '').trim())
            .filter(Boolean);
        if (statsTypes.length === 1) {
            this.#upsertFilterField('stats_type', '==', statsTypes[0], { onlyIfMissing: true });
        }

        this.#renderFilterList();
        this.#setStatus(`Applied selector: field == ${selectorField}`);
        this.#runQuery({ isAutoPreview: true });
    }

    #filterFieldList(query) {
        const q = (query || '').toLowerCase();
        const items = this.shadowRoot.querySelectorAll('.field-item');
        for (const item of items) {
            const id = (item.dataset.fieldId || '').toLowerCase();
            item.style.display = !q || id.includes(q) ? '' : 'none';
        }
        // Hide group headers when all their children are hidden
        for (const group of this.shadowRoot.querySelectorAll('.field-group')) {
            const visible = group.querySelectorAll('.field-item:not([style*="none"])');
            group.style.display = visible.length === 0 ? 'none' : '';
        }
    }

    #setAllFields(checked) {
        const buttons = this.shadowRoot.querySelectorAll('.field-item');
        this.#selectedFields.clear();
        this.#hasUserFieldSelection = true;
        for (const btn of buttons) {
            const isSelectorOnly = btn.dataset.fieldMode === 'selector';
            if (checked) {
                if (!isSelectorOnly) {
                    this.#selectedFields.add(btn.dataset.fieldId);
                    btn.classList.add('selected');
                } else {
                    btn.classList.remove('selected');
                }
            } else {
                btn.classList.remove('selected');
            }
        }
        this.#syncControlsLauncherState();
        this.#refreshGridColumnsFromSelection();
    }

    #refreshGridColumnsFromSelection() {
        if (!this.#grid || !this.#dataView) {
            return;
        }
        const rows = Array.isArray(this.#results) ? this.#results : [];
        const cols = this.#buildColumns(rows);
        this.#grid.setColumns(cols);
        this.#dataView.setItems(rows);
        this.#grid.resizeCanvas();
        this.#grid.invalidate();
        this.#syncHeaderKeyboardState();
        this.#syncControlsLauncherState();

        // Column toggles change the requested fields set, so refresh row data to populate new columns.
        if (!this.#selectedSource) {
            return;
        }
        if (this.#fieldSelectionRefreshTimer) {
            clearTimeout(this.#fieldSelectionRefreshTimer);
        }
        this.#fieldSelectionRefreshTimer = setTimeout(() => {
            this.#fieldSelectionRefreshTimer = null;
            this.#runQuery({ isAutoPreview: true });
        }, 180);
    }

    #setFieldsMode(showAllFields) {
        const next = !!showAllFields;
        if (this.#showAllFields === next) {
            this.#updateFieldsModeButtons();
            return;
        }
        this.#showAllFields = next;
        this.#applyFieldsModeSelection();
        this.#updateFieldsModeButtons();
        this.#renderFieldList(this.#schema?.field_rows || []);
        const fieldSearch = this.shadowRoot.getElementById('field-search');
        if (fieldSearch?.value) {
            this.#filterFieldList(fieldSearch.value);
        }
        this.#refreshGridColumnsFromSelection();
    }

    #applyFieldsModeSelection() {
        const rows = this.#schema?.field_rows || [];
        if (rows.length < 1) {
            return;
        }

        this.#selectedFields.clear();
        for (const row of rows) {
            const fieldId = String(row?.id || '').trim();
            if (!fieldId || this.#isRollupConfiguredSelectorField(row)) {
                continue;
            }

            if (this.#showAllFields) {
                this.#selectedFields.add(fieldId);
                continue;
            }

            if (this.#autoVisibleFieldIds.has(fieldId)) {
                this.#selectedFields.add(fieldId);
            }
        }

        if (!this.#showAllFields && this.#selectedFields.size < 1) {
            for (const row of rows) {
                const fieldId = String(row?.id || '').trim();
                if (!fieldId || this.#isRollupConfiguredSelectorField(row)) {
                    continue;
                }
                this.#selectedFields.add(fieldId);
                if (this.#selectedFields.size >= 12) {
                    break;
                }
            }
        }
    }

    #updateFieldsModeButtons() {
        const autoBtn = this.shadowRoot.getElementById('fields-mode-auto-btn');
        const allBtn = this.shadowRoot.getElementById('fields-mode-all-btn');
        if (!autoBtn || !allBtn) {
            return;
        }
        const autoActive = !this.#showAllFields;
        const allActive = this.#showAllFields;

        autoBtn.classList.toggle('is-active', autoActive);
        allBtn.classList.toggle('is-active', allActive);

        autoBtn.setAttribute('aria-pressed', autoActive ? 'true' : 'false');
        allBtn.setAttribute('aria-pressed', allActive ? 'true' : 'false');

        autoBtn.title = 'Show fields with observed values from sampled rows plus required and key fields';
        allBtn.title = 'Show every available field';
        this.#syncControlsLauncherState();
    }

    async #fetchFieldActivitySample(limit = 80) {
        if (!this.#selectedSource) {
            return [];
        }
        const url = this.#buildQueryUrl({ forceNoFields: true, limit });
        if (!url) {
            return [];
        }
        try {
            const json = await this.fetch(url);
            return Array.isArray(json) ? json : (json ? [json] : []);
        } catch (err) {
            Logger.debug('Explorer auto-fields sample query failed: ', err?.message || err);
            return [];
        }
    }

    #buildAutoVisibleFieldSet(fieldRows = [], sampledRows = []) {
        const visible = new Set();
        const fieldSet = new Set((fieldRows || []).map(f => f.id).filter(Boolean));

        const addField = (field) => {
            if (field && fieldSet.has(field)) {
                visible.add(field);
            }
        };

        const { required, requiredAny } = this.#getRequiredFilterFields();
        required.forEach(addField);
        requiredAny.forEach(addField);

        const sourceKeys = [].concat(this.#selectedSource?.keys || []).map(x => String(x || '').trim()).filter(Boolean);
        sourceKeys.forEach(addField);
        for (const row of fieldRows || []) {
            if (row?.key || row?.kind === 'key') {
                addField(row.id);
            }
        }

        for (const row of sampledRows || []) {
            for (const [ field, value ] of Object.entries(row || {})) {
                if (!fieldSet.has(field)) {
                    continue;
                }
                if (value === undefined || value === null) {
                    continue;
                }
                if (typeof value === 'string' && value.trim() === '') {
                    continue;
                }
                visible.add(field);
            }
        }

        return visible;
    }

    async #refreshAutoVisibleFields(fieldRows = []) {
        const sampledRows = await this.#fetchFieldActivitySample(80);
        this.#autoVisibleFieldIds = this.#buildAutoVisibleFieldSet(fieldRows, sampledRows);

        if (this.#autoVisibleFieldIds.size < 1) {
            for (const row of fieldRows.slice(0, 12)) {
                if (row?.id) {
                    this.#autoVisibleFieldIds.add(row.id);
                }
            }
        }
    }

    #scoreInterestingField(fieldId = '') {
        const id = String(fieldId || '').toLowerCase();
        if (!id) {
            return 0;
        }
        let score = 0;
        if (id.includes('name')) score += 5;
        if (id.includes('status')) score += 5;
        if (id.includes('uuid')) score += 6;
        if (id === 'id' || id.endsWith('_id') || id.endsWith('id')) score += 4;
        else if (id.includes('id')) score += 2;
        return score;
    }

    #autoSelectDefaultFields(fieldRows = []) {
        this.#selectedFields.clear();
        const rows = Array.isArray(fieldRows) ? fieldRows : [];
        if (rows.length < 1) {
            return;
        }

        for (const row of rows) {
            const fieldId = String(row?.id || '').trim();
            if (fieldId) {
                if (!this.#isRollupConfiguredSelectorField(row)) {
                    this.#selectedFields.add(fieldId);
                }
            }
        }
    }

    #ensureDefaultFieldSelection(fieldRows = []) {
        if (this.#selectedFields.size > 0 || this.#hasUserFieldSelection) {
            return;
        }
        const rows = Array.isArray(fieldRows) ? fieldRows : [];
        for (const row of rows) {
            const fieldId = String(row?.id || '').trim();
            if (!fieldId) {
                continue;
            }
            if (this.#isRollupConfiguredSelectorField(row)) {
                continue;
            }
            this.#selectedFields.add(fieldId);
        }
    }

    #collectObservedRowKeys(rows = [], maxRows = 200) {
        const keys = new Set();
        const slice = Array.isArray(rows) ? rows.slice(0, maxRows) : [];
        for (const row of slice) {
            for (const key of Object.keys(row || {})) {
                if (key !== 'id') {
                    keys.add(key);
                }
            }
        }
        return keys;
    }

    #isNonEmptyColumnValue(value) {
        if (value === undefined || value === null) {
            return false;
        }
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (typeof value === 'object') {
            return Object.keys(value).length > 0;
        }
        return true;
    }

    #columnValueLength(value) {
        if (value === undefined || value === null) {
            return 0;
        }
        if (typeof value === 'string') {
            return value.length;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value).length;
        }
        if (Array.isArray(value)) {
            return JSON.stringify(value.slice(0, 4)).length;
        }
        if (typeof value === 'object') {
            return JSON.stringify(value).length;
        }
        return String(value).length;
    }

    #collectColumnStats(rows = [], maxRows = 200) {
        const stats = new Map();
        const sampleRows = Array.isArray(rows) ? rows.slice(0, maxRows) : [];
        for (const row of sampleRows) {
            for (const [ key, value ] of Object.entries(row || {})) {
                if (key === 'id') {
                    continue;
                }
                if (!stats.has(key)) {
                    stats.set(key, { seen: 0, nonEmpty: 0, maxLen: 0, uuidLike: 0 });
                }
                const item = stats.get(key);
                item.seen += 1;
                if (this.#isNonEmptyColumnValue(value)) {
                    item.nonEmpty += 1;
                    item.maxLen = Math.max(item.maxLen, this.#columnValueLength(value));
                    if (this.#looksLikeUuid(value)) {
                        item.uuidLike += 1;
                    }
                }
            }
        }
        return stats;
    }

    #looksLikeUuid(value) {
        if (typeof value !== 'string') {
            return false;
        }
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
    }

    #isUuidColumn(column, stat = null) {
        const id = String(column?.id || column?.field || '').toLowerCase();
        if (id.includes('uuid') || id.includes('guid')) {
            return true;
        }
        const nonEmpty = Number(stat?.nonEmpty || 0);
        const uuidLike = Number(stat?.uuidLike || 0);
        if (nonEmpty < 1) {
            return false;
        }
        // Consider a column UUID-like when most sampled non-empty values look like UUIDs.
        return uuidLike >= Math.max(2, Math.ceil(nonEmpty * 0.5));
    }

    #columnDisplayBucket(column, stat = null) {
        const nonEmpty = Number(stat?.nonEmpty || 0);
        if (nonEmpty < 1) {
            return 2; // mostly empty columns go far right
        }
        if (this.#isUuidColumn(column, stat)) {
            return 1; // UUID-heavy columns after meaningful non-UUID columns
        }
        return 0; // primary readable columns
    }

    #getColumnPriorityHints() {
        const hints = new Map();
        const prioritized = [].concat(this.#selectedSource?.query_schema?.display_fields_priority || []);
        prioritized.forEach((field, index) => {
            const id = String(field || '').trim();
            if (id && !hints.has(id)) {
                hints.set(id, index);
            }
        });
        return hints;
    }

    #estimateColumnWidth(column, stat = null) {
        const colName = String(column?.name || column?.id || column?.field || '');
        const type = String(column?.type || '').toLowerCase();
        const headerLen = Math.max(6, Math.min(42, colName.length));
        const sampleLen = Math.max(0, Math.min(48, Number(stat?.maxLen || 0)));
        const contentChars = Math.max(headerLen, sampleLen);

        let width = 24 + (contentChars * 8);
        if ([ 'number', 'integer', 'int', 'float', 'double', 'long', 'boolean', 'bool' ].includes(type)) {
            width = Math.min(width, 170);
        }
        if (this.#isUuidColumn(column, stat)) {
            width = Math.min(width, 220);
        }

        return Math.max(110, Math.min(360, width));
    }

    #formatGridCellValue(value) {
        if (value === undefined || value === null) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        if (Array.isArray(value) || typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (_) {
                return String(value);
            }
        }
        return String(value);
    }

    #scoreColumnForDisplay(column, stat = null, hintMap = new Map(), originalIndex = 0) {
        const id = String(column?.id || column?.field || '').trim();
        const isKey = !!(column?.key || column?.kind === 'key');
        const seen = Number(stat?.seen || 0);
        const nonEmpty = Number(stat?.nonEmpty || 0);
        const fillRatio = seen > 0 ? (nonEmpty / seen) : 0;
        const hintedIndex = hintMap.has(id) ? hintMap.get(id) : Number.POSITIVE_INFINITY;

        const hintScore = Number.isFinite(hintedIndex) ? (4000 - hintedIndex) : 0;
        const keyScore = isKey ? 3000 : 0;
        const fillScore = Math.round(fillRatio * 1000);
        const densityScore = Math.min(nonEmpty, 200);
        const fallbackScore = Math.max(0, 200 - originalIndex);
        return hintScore + keyScore + fillScore + densityScore + fallbackScore;
    }

    #isProviderPickerField(field = '') {
        const lower = String(field || '').toLowerCase();
        return lower === 'providerid' || lower.endsWith('_providerid') || lower === 'orgid' || lower.endsWith('_orgid');
    }

    #getFieldMeta(field = '') {
        return (this.#schema?.field_rows || []).find(f => f.id === field) || null;
    }

    #canOpenValuePicker(field = '') {
        if (!field) {
            return false;
        }
        if (this.#isProviderPickerField(field)) {
            return true;
        }
        const meta = this.#getFieldMeta(field);
        const enumValues = this.#parseEnumValues(meta);
        if (enumValues.length > 0) {
            return true;
        }
        const type = String(meta?.type || '').toLowerCase();
        if (type === 'boolean' || type === 'bool') {
            return true;
        }
        return this.#isRollupSource(this.#selectedSource) || this.#isJournalSource(this.#selectedSource);
    }

    #getProviderPickerOptions(field = '') {
        const lower = String(field || '').toLowerCase();

        if (lower === 'orgid' || lower.endsWith('_orgid')) {
            const orgLists = [
                UserSession.get('filtered_orgs') || [],
                UserSession.get('orgs') || [],
            ];
            const orgMap = new Map();
            for (const list of orgLists) {
                for (const org of list) {
                    const id = String(org?.id || org?.orgid || '').trim();
                    if (!id || orgMap.has(id)) {
                        continue;
                    }
                    const label = String(org?.displayname || org?.name || org?.providername || id);
                    orgMap.set(id, { value: id, label });
                }
                if (orgMap.size > 0) {
                    break;
                }
            }
            return Array.from(orgMap.values()).sort((a, b) => a.label.localeCompare(b.label));
        }

        const providerLists = [
            UserSession.get('selected_providers') || [],
            UserSession.get('filtered_providers') || [],
            UserSession.get('providers') || [],
            UserSession.get('user_providers') || [],
        ];
        const providerMap = new Map();
        for (const list of providerLists) {
            for (const p of list) {
                const id = String(p?.providerid || p?.id || '').trim();
                if (!id || providerMap.has(id)) {
                    continue;
                }
                const providerName = String(p?.displayname || p?.name || p?.providername || id);
                const orgName = String(p?.orgdisplayname || p?.orgname || p?.orgid || '').trim();
                const label = orgName ? `${providerName} (${orgName})` : providerName;
                providerMap.set(id, { value: id, label });
            }
            if (providerMap.size > 0) {
                break;
            }
        }
        return Array.from(providerMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    async #probePickerFieldValues(field = '') {
        if (!this.#selectedSource || !field) {
            return [];
        }

        const startVal = this.shadowRoot.getElementById('start-time')?.value || '';
        const endVal = this.shadowRoot.getElementById('end-time')?.value || '';
        const cacheKey = [ this.#selectedSource?.id || this.#selectedSource?.path || '', field, startVal, endVal ].join('|');
        if (this.#fieldValueOptionsCache.has(cacheKey)) {
            return this.#fieldValueOptionsCache.get(cacheKey);
        }

        const params = new URLSearchParams();
        params.set('rid', Math.random().toString(36).slice(2));
        params.set('fields', field);
        params.set('limit', '240');
        if (startVal) params.set('starttime', String(new Date(startVal).getTime()));
        if (endVal) params.set('endtime', String(new Date(endVal).getTime()));

        const url = `/${this.#selectedSource.path}?${params.toString()}`;
        const options = [];
        try {
            const json = await this.fetch(url);
            const rows = Array.isArray(json) ? json : (json ? [json] : []);
            const seen = new Set();
            for (const row of rows) {
                const value = row?.[field];
                if (value === undefined || value === null || value === '') {
                    continue;
                }
                const normalized = String(value);
                if (seen.has(normalized)) {
                    continue;
                }
                seen.add(normalized);
                options.push({ value: normalized, label: normalized });
                if (options.length >= 60) {
                    break;
                }
            }
        } catch (err) {
            Logger.debug('Explorer value picker probe failed: ', err?.message || err);
        }

        this.#fieldValueOptionsCache.set(cacheKey, options);
        return options;
    }

    async #getPickerOptionsForField(field = '') {
        if (this.#isProviderPickerField(field)) {
            return this.#getProviderPickerOptions(field);
        }

        const meta = this.#getFieldMeta(field);
        const enumValues = this.#parseEnumValues(meta);
        if (enumValues.length > 0) {
            return enumValues.map(v => ({ value: String(v), label: String(v) }));
        }

        const type = String(meta?.type || '').toLowerCase();
        if (type === 'boolean' || type === 'bool') {
            return [ { value: 'true', label: 'true' }, { value: 'false', label: 'false' } ];
        }

        return await this.#probePickerFieldValues(field);
    }

    async #openValuePicker(filter) {
        if (!filter || !this.#canOpenValuePicker(filter.field)) {
            return;
        }

        const options = await this.#getPickerOptionsForField(filter.field);
        const modal = this.shadowRoot.getElementById('provider-picker-modal');
        const title = this.shadowRoot.getElementById('provider-picker-title');
        const subtitle = this.shadowRoot.querySelector('.provider-picker-subtitle');
        const select = this.shadowRoot.getElementById('provider-picker-select');
        if (!modal || !title || !select) {
            return;
        }

        if (!options || options.length < 1) {
            this.#setStatus(`No quick-pick values available yet for ${filter.field}`);
            return;
        }

        this.#pickerFilterId = filter.id;
        this.#pickerField = filter.field;
        title.textContent = this.#isProviderPickerField(this.#pickerField)
            ? (this.#pickerField.toLowerCase().includes('org') ? 'Choose organizations' : 'Choose providers')
            : `Choose values for ${this.#pickerField}`;
        if (subtitle) {
            subtitle.textContent = `Use Cmd/Ctrl-click to choose multiple values for ${this.#pickerField}.`;
        }

        const selectedValues = new Set(
            String(filter.value || '')
                .split(',')
                .map(x => x.trim())
                .filter(Boolean)
        );

        select.innerHTML = '';
        for (const option of options) {
            const el = document.createElement('option');
            el.value = option.value;
            el.textContent = option.label;
            if (selectedValues.has(option.value)) {
                el.selected = true;
            }
            select.appendChild(el);
        }

        modal.classList.remove('hidden');
        select.focus();
    }

    #closeValuePicker() {
        const modal = this.shadowRoot.getElementById('provider-picker-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
        this.#pickerFilterId = null;
        this.#pickerField = '';
    }

    #applyValuePickerSelection() {
        if (!this.#pickerFilterId) {
            this.#closeValuePicker();
            return;
        }

        const select = this.shadowRoot.getElementById('provider-picker-select');
        if (!select) {
            this.#closeValuePicker();
            return;
        }

        const selected = Array.from(select.selectedOptions || []).map(o => o.value).filter(Boolean);
        if (selected.length < 1) {
            this.#closeValuePicker();
            return;
        }

        const filter = this.#filters.find(f => f.id === this.#pickerFilterId);
        if (!filter) {
            this.#closeValuePicker();
            return;
        }

        if (selected.length === 1) {
            filter.op = '==';
            filter.value = selected[0];
        } else {
            filter.op = 'in';
            filter.value = selected.join(',');
        }
        this.#renderFilterList();
        this.#closeValuePicker();
    }

    // ── Filter builder ──────────────────────────────────────────────────────

    #addFilter() {
        this.#filters.push({ id: ++this.#filterIdCounter, field: '', op: '==', value: '' });
        this.#renderFilterList();
    }

    #removeFilter(filterId) {
        this.#filters = this.#filters.filter(f => f.id !== filterId);
        this.#renderFilterList();
    }

    #clearFilters() {
        if (this.#filters.length < 1) {
            return;
        }
        this.#filters = [];
        this.#renderFilterList();
        this.#setStatus('Filters cleared');
        if (this.#selectedSource) {
            this.#runQuery({ isAutoPreview: true });
        }
    }

    #focusFilterRow(filterId) {
        if (!filterId) {
            return;
        }
        const row = this.shadowRoot.querySelector(`.filter-row[data-filter-id="${filterId}"]`);
        if (!row) {
            return;
        }
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const valueInput = row.querySelector('.filter-value');
        if (valueInput) {
            valueInput.focus();
            valueInput.select();
        }
    }

    #renderFilterList() {
        const filterList = this.shadowRoot.getElementById('filter-list');
        filterList.innerHTML = '';
        const filterableFields = (this.#schema?.field_rows || []).filter(f => f.filterable !== false);

        for (const filter of this.#filters) {
            const row = document.createElement('div');
            row.className = 'filter-row';
            row.dataset.filterId = String(filter.id);

            // Field select
            const fieldSel = document.createElement('select');
            fieldSel.className = 'filter-field';
            fieldSel.innerHTML = `<option value="">field…</option>` +
                filterableFields.map(f =>
                    `<option value="${f.id}" ${f.id === filter.field ? 'selected' : ''}>${f.name || f.id}</option>`
                ).join('');
            fieldSel.addEventListener('change', () => { filter.field = fieldSel.value; });

            // Operator select
            const opSel = document.createElement('select');
            opSel.className = 'filter-op';
            opSel.innerHTML = OPERATORS.map(o =>
                `<option value="${o.value}" ${o.value === filter.op ? 'selected' : ''}>${o.label}</option>`
            ).join('');
            opSel.addEventListener('change', () => { filter.op = opSel.value; });

            // Value input
            const valueInput = document.createElement('input');
            valueInput.type = 'text';
            valueInput.className = 'filter-value';
            valueInput.placeholder = 'value';
            valueInput.value = filter.value;
            valueInput.addEventListener('input', () => { filter.value = valueInput.value; });
            valueInput.addEventListener('keydown', (ev) => {
                if (ev.key === 'Enter') this.#runQuery();
            });
            valueInput.addEventListener('click', () => {
                if (this.#canOpenValuePicker(filter.field)) {
                    this.#openValuePicker(filter);
                }
            });

            // Remove button
            const removeBtn = document.createElement('button');
            removeBtn.className = 'filter-remove';
            removeBtn.textContent = '×';
            removeBtn.addEventListener('click', () => this.#removeFilter(filter.id));

            const pickBtn = document.createElement('button');
            pickBtn.type = 'button';
            pickBtn.className = 'filter-pick-btn';
            pickBtn.textContent = 'Pick';
            pickBtn.title = 'Choose values';
            pickBtn.style.display = this.#canOpenValuePicker(filter.field) ? '' : 'none';
            pickBtn.addEventListener('click', () => this.#openValuePicker(filter));

            fieldSel.addEventListener('change', () => {
                pickBtn.style.display = this.#canOpenValuePicker(filter.field) ? '' : 'none';
            });

            row.appendChild(fieldSel);
            row.appendChild(opSel);
            row.appendChild(valueInput);
            row.appendChild(pickBtn);
            row.appendChild(removeBtn);
            filterList.appendChild(row);
        }
        this.#syncControlsLauncherState();
    }

    // ── Aggregation ─────────────────────────────────────────────────────────

    #populateAggrFieldSelect(fieldRows) {
        const aggrField = this.shadowRoot.getElementById('aggr-field');
        const numeric = fieldRows.filter(f => ['number', 'integer', 'int', 'float', 'double', 'long'].includes(f.type));
        const candidates = numeric.length > 0 ? numeric : fieldRows;
        aggrField.innerHTML = `<option value="">— field —</option>` +
            candidates.map(f => `<option value="${f.id}">${f.name || f.id}</option>`).join('');
        this.#aggrField = '';
    }

    #updateAggrFieldVisibility() {
        const needsField = ['sum', 'avg', 'min', 'max'].includes(this.#aggrOp);
        this.shadowRoot.getElementById('aggr-field').classList.toggle('hidden', !needsField);
    }

    #resetAggregation() {
        const aggrOp = this.shadowRoot.getElementById('aggr-op');
        aggrOp.value = '';
        this.#aggrOp = '';
        this.#aggrField = '';
        this.shadowRoot.getElementById('aggr-field').classList.add('hidden');
        this.#syncControlsLauncherState();
    }

    // ── Query execution ─────────────────────────────────────────────────────

    #buildQueryUrl({ forceNoFields = false, limit = null } = {}) {
        if (!this.#selectedSource) return null;
        const params = new URLSearchParams();
        params.set('rid', Math.random().toString(36).slice(2));

        if (limit) {
            params.set('limit', String(limit));
        }

        // Build filter expression
        const valid = this.#filters.filter(f => f.field && f.op);
        const scopeFilter = this.#buildScopeFilterAst();
        if (valid.length === 1) {
            const f = valid[0];
            const userFilter = [f.field, f.op, this.#parseValue(f.op, f.value)];
            if (scopeFilter) {
                params.set('filter', JSON.stringify([ userFilter, 'and', scopeFilter ]));
            } else {
                params.set('filter', JSON.stringify(userFilter));
            }
        } else if (valid.length > 1) {
            const expr = [];
            valid.forEach((f, i) => {
                expr.push([f.field, f.op, this.#parseValue(f.op, f.value)]);
                if (i < valid.length - 1) expr.push('and');
            });
            if (scopeFilter) {
                params.set('filter', JSON.stringify([ expr, 'and', scopeFilter ]));
            } else {
                params.set('filter', JSON.stringify(expr));
            }
        } else if (scopeFilter) {
            params.set('filter', JSON.stringify(scopeFilter));
        }

        // Aggregation
        if (this.#aggrOp) {
            const needsField = ['sum', 'avg', 'min', 'max'].includes(this.#aggrOp);
            const aggrValue = needsField && this.#aggrField
                ? `${this.#aggrOp}:${this.#aggrField}`
                : this.#aggrOp;
            params.set('aggregate', aggrValue);
        }

        // Time range
        const startVal = this.shadowRoot.getElementById('start-time').value;
        const endVal   = this.shadowRoot.getElementById('end-time').value;
        if (startVal) params.set('starttime', String(new Date(startVal).getTime()));
        if (endVal)   params.set('endtime',   String(new Date(endVal).getTime()));

        // Adornments
        const validAdornments = this.#adornments.filter(a => a.sourceId && this.#isAdornmentKeyComplete(a) && a.fields?.size > 0);
        if (validAdornments.length > 0) {
            const adornmentsArray = validAdornments.map(a => {
                const { key, source_key } = this.#getAdornmentKeyFields(a);
                const entry = {
                    source: a.sourceId,
                    key: key.length === 1 ? key[0] : key,
                    fields: Array.from(a.fields),
                };
                // Only include source_key if it differs from key
                const keysMatch = key.length === source_key.length && key.every((k, i) => k === source_key[i]);
                if (!keysMatch) {
                    entry.source_key = source_key.length === 1 ? source_key[0] : source_key;
                }
                return entry;
            });
            params.set('adornments', JSON.stringify(adornmentsArray));
        }

        // GroupBy
        const activeGroupBy = this.#groupByFields.filter(g => g.field).map(g => g.field);
        if (activeGroupBy.length > 0) {
            params.set('groupby', JSON.stringify(activeGroupBy));
        }

        // Append fields outside URLSearchParams to avoid comma being percent-encoded to %2C
        let url = `/${this.#selectedSource.path}?${params.toString()}`;
        if (!forceNoFields && !this.#showAllFields && this.#selectedFields.size > 0) {
            url += `&fields=${Array.from(this.#selectedFields).join(',')}`;
        }
        return url;
    }

    #parseValue(op, raw) {
        if (op === 'in' || op === 'not in') {
            return raw.split(',').map(v => v.trim()).filter(Boolean);
        }
        if (raw === '') return '';
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        const n = Number(raw);
        return Number.isFinite(n) ? n : raw;
    }

    // Use POST when the query URL exceeds safe GET length limits (e.g. many fields)
    async #fetchQuery(url) {
        const MAX_GET_LENGTH = 4096;
        if (url.length <= MAX_GET_LENGTH) {
            return this.fetch(url);
        }
        const qIdx = url.indexOf('?');
        const path = qIdx >= 0 ? url.slice(0, qIdx) : url;
        const qs = qIdx >= 0 ? url.slice(qIdx + 1) : '';
        const payload = Object.fromEntries(new URLSearchParams(qs));
        return this.fetch(path, { method: 'POST', payload });
    }

    async #runQuery({ isAutoPreview = false } = {}) {
        if (!this.#selectedSource) {
            this.#setStatus('Please select a data source first');
            return { ok: false, rows: 0, error: 'NO_SOURCE_SELECTED' };
        }
        const url = this.#buildQueryUrl();
        if (!url) return { ok: false, rows: 0, error: 'QUERY_URL_UNAVAILABLE' };

        this.#setRunning(true);
        this.shadowRoot.getElementById('query-display').textContent = url;

        try {
            const json = await this.#fetchQuery(url);
            const rows = Array.isArray(json) ? json : (json ? [json] : []);
            rows.forEach((row, i) => { row.id = `row_${i}`; });
            this.#results = rows;
            this.#renderJsonView(rows);

            const cols = this.#buildColumns(rows);
            this.#grid.setColumns(cols);
            this.#dataView.setItems(rows);
            this.#grid.resizeCanvas();
            this.#grid.invalidate();
            this.#syncHeaderKeyboardState();

            const summary = `${rows.length} row${rows.length === 1 ? '' : 's'} returned`;
            this.#setStatus(isAutoPreview ? `Preview loaded — ${summary}` : summary);
            return { ok: true, rows: rows.length };
        } catch(err) {
            Logger.error("Explorer: query failed: ", err);
            const msg = err?.status ? `${err.status}: ${err.message || err}` : (err.message || String(err));
            this.#setStatus(isAutoPreview ? `Preview query failed — ${msg}` : `Query failed — ${msg}`);
            return { ok: false, rows: 0, error: msg };
        } finally {
            this.#setRunning(false);
        }
    }

    #buildColumns(rows) {
        const rawFieldRows = this.#schema?.field_rows || [];
        const fieldRows = this.#deduplicateFieldRows(rawFieldRows);
        this.#ensureDefaultFieldSelection(fieldRows);
        if (this.#hasUserFieldSelection && this.#selectedFields.size === 0) {
            return [];
        }
        const observedKeys = this.#collectObservedRowKeys(rows);
        const columnStats = this.#collectColumnStats(rows);
        const priorityHints = this.#getColumnPriorityHints();
        const isRollup = this.#isRollupSource(this.#selectedSource);
        let cols;

        if (fieldRows.length > 0) {
            // Use schema field_rows, filtered to selected if any are chosen
            if (this.#selectedFields.size > 0) {
                // Preserve selectedFields insertion order (adorn fields prepended = left side)
                const fieldMap = new Map(fieldRows.map(f => [f.id, f]));
                cols = [];
                for (const id of this.#selectedFields) {
                    const row = fieldMap.get(id);
                    if (row) cols.push(row);
                }
            } else {
                cols = [...fieldRows];
            }

            // Rollup configured dimensions are selector metadata, not direct row columns.
            if (isRollup) {
                cols = cols.filter(f => !this.#isRollupConfiguredSelectorField(f));
            }

            // For rollups, drop selected schema columns that never appear in observed rows.
            if (isRollup && observedKeys.size > 0) {
                cols = cols.filter(f => {
                    const id = String(f?.id || '').trim();
                    if (!id) {
                        return false;
                    }
                    if (observedKeys.has(id)) {
                        return true;
                    }
                    return !!(f?.key || f?.kind === 'key');
                });
            }

            // Append any extra columns present in data but not in schema (e.g. aggregation result keys)
            if (observedKeys.size > 0) {
                const knownIds = new Set(cols.map(f => f.id));
                for (const key of observedKeys) {
                    if (!knownIds.has(key)) {
                        if (!this.#showAllFields) {
                            const stat = columnStats.get(key);
                            const nonEmpty = Number(stat?.nonEmpty || 0);
                            if (nonEmpty < 1) {
                                continue;
                            }
                        }
                        cols.push({ id: key, name: key, field: key });
                    }
                }
            }
        } else {
            // Auto-detect from data when no schema
            const keys = new Set();
            for (const row of rows.slice(0, 30)) {
                for (const key of Object.keys(row)) {
                    if (key !== 'id') keys.add(key);
                }
            }
            cols = Array.from(keys).map(k => ({ id: k, name: k, field: k }));
        }

        const seenColumnIds = new Set();
        cols = cols.filter(col => {
            const colId = String(col?.id || col?.field || '').trim();
            if (!colId || seenColumnIds.has(colId)) {
                return false;
            }
            seenColumnIds.add(colId);
            return true;
        });

        cols = cols
            .map((col, index) => {
                const colId = String(col?.id || col?.field || '').trim();
                const stat = columnStats.get(colId) || null;
                return {
                    ...col,
                    __bucket: this.#columnDisplayBucket(col, stat),
                    __score: this.#scoreColumnForDisplay(col, stat, priorityHints, index),
                    __width: this.#estimateColumnWidth(col, stat),
                };
            })
            .sort((a, b) => {
                if (a.__bucket !== b.__bucket) {
                    return a.__bucket - b.__bucket;
                }
                if (a.__score !== b.__score) {
                    return b.__score - a.__score;
                }
                return String(a?.id || '').localeCompare(String(b?.id || ''));
            });

        return cols.map(f => ({
            id:       f.id,
            name:     f.name || f.id,
            field:    f.field || f.id,
            sortable: true,
            minWidth: 90,
            width: Number(f.__width || 140),
            formatter: (_row, _cell, value) => {
                const rendered = this.#formatGridCellValue(value);
                const full = String(rendered || '');
                const maxChars = 120;
                const display = full.length > maxChars ? `${full.slice(0, maxChars - 1)}...` : full;
                return `<span class="explorer-cell-truncate" title="${SlickGridUtils.escapeHtml(full)}">${SlickGridUtils.escapeHtml(display)}</span>`;
            },
        }));
    }

    // ── Wizard: Visualization ──────────────────────────────────────────────

    #setVizType(type = 'table') {
        this.#vizType = type;
        const sd = this.shadowRoot;
        sd.querySelectorAll('.viz-type-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.viz === type);
        });
        this.#syncVizSections();
        this.#syncPreviewVisibility();
    }

    #restoreTablePreview() {
        const sd = this.shadowRoot;
        const tableTarget = sd.getElementById('table-target');
        const chartTarget = sd.getElementById('chart-preview-target');
        const metricTarget = sd.getElementById('metric-preview-target');
        if (tableTarget) tableTarget.classList.remove('hidden');
        if (chartTarget) chartTarget.classList.add('hidden');
        if (metricTarget) metricTarget.classList.add('hidden');
        if (this.#grid) {
            requestAnimationFrame(() => this.#grid.resizeCanvas());
        }
    }

    #syncVizSections() {
        const sd = this.shadowRoot;
        const isChart = this.#vizType === 'line' || this.#vizType === 'bar';
        const isMetric = this.#vizType === 'metric';

        const axisSection = sd.getElementById('viz-axis-section');
        const metricSection = sd.getElementById('viz-metric-section');
        if (axisSection) axisSection.classList.toggle('hidden', !isChart);
        if (metricSection) metricSection.classList.toggle('hidden', !isMetric);

        // Populate axis selects with current fields
        if (isChart) {
            this.#populateAxisSelects();
        }
        if (isMetric) {
            this.#populateMetricFieldSelect();
        }
    }

    #syncPreviewVisibility() {
        const sd = this.shadowRoot;
        const isChart = this.#vizType === 'line' || this.#vizType === 'bar';
        const isMetric = this.#vizType === 'metric';
        const isTable = this.#vizType === 'table';

        const tableTarget = sd.getElementById('table-target');
        const chartTarget = sd.getElementById('chart-preview-target');
        const metricTarget = sd.getElementById('metric-preview-target');

        // Only show the active preview pane (unless user explicitly chose JSON/Schema view)
        const isDataView = this.#resultsView === 'card';
        if (tableTarget) tableTarget.classList.toggle('hidden', !isTable || !isDataView);
        if (chartTarget) chartTarget.classList.toggle('hidden', !isChart || !isDataView);
        if (metricTarget) metricTarget.classList.toggle('hidden', !isMetric || !isDataView);

        if (isChart && isDataView) {
            this.#renderChartPreview();
        }
        if (isMetric && isDataView) {
            this.#renderMetricPreview();
        }
        // Resize the grid if switching back to table
        if (isTable && isDataView && this.#grid) {
            requestAnimationFrame(() => this.#grid.resizeCanvas());
        }
    }

    // ── Chart Preview ───────────────────────────────────────────────────────

    #renderChartPreview() {
        const sd = this.shadowRoot;
        const container = sd.getElementById('chart-preview-target');
        if (!container) return;

        const data = this.#results;
        if (!data || data.length === 0) {
            container.innerHTML = '<div style="padding:20px;text-align:center;color:#8a9aaa;">No data to chart — run a query first</div>';
            this.#disposeChartPreview();
            return;
        }

        const xField = sd.getElementById('viz-x-axis')?.value || '';
        const ySelect = sd.getElementById('viz-y-axis');
        const yFields = ySelect ? Array.from(ySelect.selectedOptions || []).map(o => o.value) : [];

        if (!xField || yFields.length === 0) {
            container.innerHTML = '<div style="padding:20px;text-align:center;color:#8a9aaa;">Select X-axis and at least one Y-axis field</div>';
            this.#disposeChartPreview();
            return;
        }

        // Clear any message text
        container.textContent = '';

        // Detect if X-axis is timestamp (numeric ms)
        const isTimeSeries = this.#isTimeSeriesSource(this.#selectedSource);
        const isTimeAxis = isTimeSeries && (xField.includes('timestamp') || xField.includes('time'));

        // Sort data by X
        const sorted = [...data].sort((a, b) => {
            const va = isTimeAxis ? parseInt(a[xField]) : a[xField];
            const vb = isTimeAxis ? parseInt(b[xField]) : b[xField];
            return va < vb ? -1 : va > vb ? 1 : 0;
        });

        // Build series
        const chartType = this.#vizType === 'bar' ? 'bar' : 'line';
        const series = yFields.map(field => ({
            name: field,
            type: chartType,
            data: sorted.map(row => {
                const x = isTimeAxis ? new Date(parseInt(row[xField])) : row[xField];
                return [x, Number(row[field]) || 0];
            }),
            showSymbol: sorted.length < 100,
            smooth: chartType === 'line' ? 0.3 : false,
        }));

        // Build options
        const options = {
            animation: false,
            useUTC: true,
            tooltip: { trigger: 'axis' },
            legend: yFields.length > 1 ? { show: true, data: yFields } : { show: false },
            grid: { left: 60, right: 30, top: 40, bottom: 40, containLabel: false },
            xAxis: isTimeAxis
                ? { type: 'time', boundaryGap: false }
                : { type: 'category', data: sorted.map(r => String(r[xField] ?? '')), boundaryGap: chartType === 'bar' },
            yAxis: { type: 'value' },
            series,
        };

        // Read palette from CSS custom properties
        const cs = getComputedStyle(this);
        const palette = [1,2,3,4,5,6].map(i => cs.getPropertyValue(`--chart-${i}`).trim()).filter(Boolean);
        if (palette.length > 0) options.color = palette;

        // Initialize or reuse ECharts instance
        if (!this.#previewChart) {
            this.#previewChart = echarts.init(container);
        }
        this.#previewChart.setOption(options, true);
        // Defer resize so container has layout dimensions
        requestAnimationFrame(() => this.#previewChart?.resize());
    }

    #disposeChartPreview() {
        if (this.#previewChart) {
            this.#previewChart.dispose();
            this.#previewChart = null;
        }
    }

    // ── Metric Preview ──────────────────────────────────────────────────────

    #renderMetricPreview() {
        const sd = this.shadowRoot;
        const container = sd.getElementById('metric-preview-target');
        if (!container) return;

        const data = this.#results;
        const field = sd.getElementById('viz-metric-field')?.value || '';
        const title = sd.getElementById('viz-metric-title')?.value?.trim() || '';
        const suffix = sd.getElementById('viz-metric-suffix')?.value?.trim() || '';

        container.innerHTML = '';

        if (!data || data.length === 0 || !field) {
            container.innerHTML = '<div style="padding:20px;text-align:center;color:#8a9aaa;">Select a value field to preview the metric</div>';
            return;
        }

        // Compute the metric value (first row by default)
        const value = data[0]?.[field] ?? '—';

        const card = document.createElement('div');
        card.className = 'metric-preview-card';

        const valEl = document.createElement('div');
        valEl.className = 'metric-preview-value';
        valEl.textContent = typeof value === 'number' ? value.toLocaleString() : value;
        card.appendChild(valEl);

        if (title) {
            const titleEl = document.createElement('div');
            titleEl.className = 'metric-preview-title';
            titleEl.textContent = title;
            card.appendChild(titleEl);
        }

        if (suffix) {
            const suffEl = document.createElement('div');
            suffEl.className = 'metric-preview-suffix';
            suffEl.textContent = suffix;
            card.appendChild(suffEl);
        }

        container.appendChild(card);
    }

    #populateAxisSelects() {
        const sd = this.shadowRoot;
        const xSelect = sd.getElementById('viz-x-axis');
        const ySelect = sd.getElementById('viz-y-axis');
        if (!xSelect || !ySelect) return;

        const fields = this.#getActiveFieldIds();
        const currentX = xSelect.value;
        const currentY = Array.from(ySelect.selectedOptions || []).map(o => o.value);
        const fieldMeta = this.#getFieldMetaMap();
        const keyFields = this.#getSchemaKeyFieldIds();
        const isTimeSeries = this.#isTimeSeriesSource(this.#selectedSource);

        // Classify fields
        const numericFields = [];
        const timeFields = [];
        for (const f of fields) {
            const meta = fieldMeta.get(f);
            const type = String(meta?.type || '').toLowerCase();
            if (f.includes('timestamp') || f.includes('time') || type === 'timestamp' || type === 'datetime') {
                timeFields.push(f);
            } else if (type === 'number' || type === 'integer' || type === 'float' || type === 'double' || type === 'long') {
                numericFields.push(f);
            } else if (!meta && this.#results.length > 0) {
                // Infer from data if no schema type
                const sample = this.#results[0]?.[f];
                if (typeof sample === 'number') numericFields.push(f);
            }
        }

        // Auto-detect best X: prefer timestamp fields for time-series, else first key field
        let bestX = currentX;
        if (!bestX) {
            if (isTimeSeries && timeFields.length > 0) {
                bestX = timeFields[0];
            } else if (keyFields.size > 0) {
                bestX = fields.find(f => keyFields.has(f)) || fields[0] || '';
            } else {
                bestX = fields[0] || '';
            }
        }

        // Auto-detect best Y: prefer numeric non-key, non-time fields
        let bestY = currentY;
        if (bestY.length === 0 && numericFields.length > 0) {
            // Pick up to 3 numeric fields that aren't the X axis
            bestY = numericFields.filter(f => f !== bestX).slice(0, 3);
            if (bestY.length === 0) bestY = numericFields.slice(0, 1);
        }

        xSelect.innerHTML = '';
        ySelect.innerHTML = '';
        for (const f of fields) {
            const xOpt = document.createElement('option');
            xOpt.value = f;
            xOpt.textContent = f;
            if (f === bestX) xOpt.selected = true;
            xSelect.appendChild(xOpt);

            const yOpt = document.createElement('option');
            yOpt.value = f;
            yOpt.textContent = f;
            if (bestY.includes(f)) yOpt.selected = true;
            ySelect.appendChild(yOpt);
        }
    }

    #populateMetricFieldSelect() {
        const sd = this.shadowRoot;
        const select = sd.getElementById('viz-metric-field');
        if (!select) return;

        const fields = this.#getActiveFieldIds();
        const current = select.value;
        const fieldMeta = this.#getFieldMetaMap();

        // Auto-detect best metric field: prefer first numeric field
        let bestField = current;
        if (!bestField) {
            for (const f of fields) {
                const meta = fieldMeta.get(f);
                const type = String(meta?.type || '').toLowerCase();
                if (type === 'number' || type === 'integer' || type === 'float' || type === 'double' || type === 'long') {
                    bestField = f;
                    break;
                }
                if (!meta && this.#results.length > 0 && typeof this.#results[0]?.[f] === 'number') {
                    bestField = f;
                    break;
                }
            }
            if (!bestField) bestField = fields[0] || '';
        }

        select.innerHTML = '';
        for (const f of fields) {
            const opt = document.createElement('option');
            opt.value = f;
            opt.textContent = f;
            if (f === bestField) opt.selected = true;
            select.appendChild(opt);
        }
    }

    #getActiveFieldIds() {
        if (this.#selectedFields.size > 0) {
            return Array.from(this.#selectedFields);
        }
        // Fall back to deduplicated schema field list
        const fieldRows = this.#schema?.field_rows || [];
        if (fieldRows.length > 0) {
            return this.#deduplicateFieldRows(fieldRows).map(r => r.id).filter(Boolean);
        }
        // Last resort: raw fields array
        const fields = this.#schema?.fields || [];
        return fields.map(r => r?.id || r?.field || r || '').filter(Boolean);
    }

    #getOutputFieldIds() {
        // Active fields minus join-only exclusions — used for card config output
        return this.#getActiveFieldIds().filter(f => !this.#joinOnlyFields.has(f));
    }

    // ── Wizard: Customize ───────────────────────────────────────────────────

    static FORMATTERS = [
        { value: '',                  label: 'None (raw value)' },
        { value: 'dateFormatter',     label: 'Date' },
        { value: 'durationFormatter', label: 'Duration (h/m)' },
        { value: 'percentFormatter',  label: 'Percent' },
        { value: 'clickableFormatter', label: 'Clickable' },
        { value: 'providerNameFormatter', label: 'Provider Name' },
        { value: 'connectedFormatter', label: 'Connected Status' },
        { value: 'onlineFormatter',   label: 'Online Status' },
        { value: 'assignedFormatter', label: 'Assigned Status' },
    ];

    #syncCustomizeSections() {
        const sd = this.shadowRoot;
        const isTable = this.#vizType === 'table';
        const isChart = this.#vizType === 'line' || this.#vizType === 'bar';
        const isMetric = this.#vizType === 'metric';

        const tableSection = sd.getElementById('customize-table-section');
        const chartSection = sd.getElementById('customize-chart-section');
        const metricSection = sd.getElementById('customize-metric-section');
        if (tableSection) tableSection.classList.toggle('hidden', !isTable);
        if (chartSection) chartSection.classList.toggle('hidden', !isChart);
        if (metricSection) metricSection.classList.toggle('hidden', !isMetric);

        if (isTable) {
            // Initialize column configs if empty or stale
            // Use the same deduplication + join-only filtering as the table view
            const rawFieldRows = this.#schema?.field_rows || [];
            const validIds = new Set(this.#deduplicateFieldRows(rawFieldRows).map(r => r.id));
            const fields = this.#getOutputFieldIds().filter(f => validIds.size === 0 || validIds.has(f));
            if (this.#columnConfigs.length === 0 || !fields.every(f => this.#columnConfigs.some(c => c.field === f))) {
                this.#initColumnConfigs(fields);
            }
            this.#renderColumnConfigList();

            // Populate sort column select
            const sortCol = sd.getElementById('custom-sort-col');
            if (sortCol) {
                const current = sortCol.value;
                sortCol.innerHTML = '<option value="">None</option>';
                for (const f of fields) {
                    const opt = document.createElement('option');
                    opt.value = f;
                    opt.textContent = f;
                    if (f === current) opt.selected = true;
                    sortCol.appendChild(opt);
                }
            }
        }
    }

    #initColumnConfigs(fields = []) {
        // Preserve existing configs for fields that still exist; add new ones
        const existing = new Map(this.#columnConfigs.map(c => [c.field, c]));
        this.#columnConfigs = fields.map(f => existing.get(f) || {
            field: f,
            label: f,
            formatter: '',
            width: '',
            frozen: false,
        });
    }

    #renderColumnConfigList() {
        const container = this.shadowRoot.getElementById('col-config-list');
        if (!container) return;
        container.innerHTML = '';

        for (const col of this.#columnConfigs) {
            const row = document.createElement('div');
            row.className = 'col-config-row';

            // Field name (read-only)
            const nameEl = document.createElement('div');
            nameEl.style.gridColumn = '1 / -1';
            nameEl.style.display = 'flex';
            nameEl.style.alignItems = 'center';
            nameEl.style.gap = '6px';

            const fieldLabel = document.createElement('span');
            fieldLabel.className = 'col-config-field-name';
            fieldLabel.textContent = col.field;
            fieldLabel.title = col.field;
            nameEl.appendChild(fieldLabel);
            row.appendChild(nameEl);

            // Label input
            const labelWrap = document.createElement('div');
            const labelLbl = document.createElement('span');
            labelLbl.className = 'col-config-label';
            labelLbl.textContent = 'Label';
            labelWrap.appendChild(labelLbl);
            const labelInput = document.createElement('input');
            labelInput.type = 'text';
            labelInput.value = col.label;
            labelInput.placeholder = col.field;
            labelInput.addEventListener('change', () => { col.label = labelInput.value || col.field; });
            labelWrap.appendChild(labelInput);
            row.appendChild(labelWrap);

            // Formatter select
            const fmtWrap = document.createElement('div');
            const fmtLbl = document.createElement('span');
            fmtLbl.className = 'col-config-label';
            fmtLbl.textContent = 'Format';
            fmtWrap.appendChild(fmtLbl);
            const fmtSelect = document.createElement('select');
            for (const fmt of HSExplorer.FORMATTERS) {
                const opt = document.createElement('option');
                opt.value = fmt.value;
                opt.textContent = fmt.label;
                if (fmt.value === col.formatter) opt.selected = true;
                fmtSelect.appendChild(opt);
            }
            fmtSelect.addEventListener('change', () => { col.formatter = fmtSelect.value; });
            fmtWrap.appendChild(fmtSelect);
            row.appendChild(fmtWrap);

            // Width input
            const widthWrap = document.createElement('div');
            const widthLbl = document.createElement('span');
            widthLbl.className = 'col-config-label';
            widthLbl.textContent = 'Width';
            widthWrap.appendChild(widthLbl);
            const widthInput = document.createElement('input');
            widthInput.type = 'number';
            widthInput.min = '0';
            widthInput.placeholder = 'auto';
            widthInput.value = col.width || '';
            widthInput.style.width = '60px';
            widthInput.addEventListener('change', () => { col.width = widthInput.value ? parseInt(widthInput.value) : ''; });
            widthWrap.appendChild(widthInput);
            row.appendChild(widthWrap);

            container.appendChild(row);
        }
    }

    // ── Wizard: GroupBy ─────────────────────────────────────────────────────

    #addGroupBy() {
        this.#groupByFields.push({ id: ++this.#groupByIdCounter, field: '' });
        this.#renderGroupByList();
    }

    #removeGroupBy(id) {
        this.#groupByFields = this.#groupByFields.filter(g => g.id !== id);
        this.#renderGroupByList();
    }

    #renderGroupByList() {
        const container = this.shadowRoot.getElementById('groupby-list');
        if (!container) return;
        container.innerHTML = '';

        const fields = this.#getActiveFieldIds();
        for (const item of this.#groupByFields) {
            const row = document.createElement('div');
            row.className = 'groupby-row';

            const select = document.createElement('select');
            select.className = 'groupby-field';
            select.innerHTML = '<option value="">Select field...</option>';
            for (const f of fields) {
                const opt = document.createElement('option');
                opt.value = f;
                opt.textContent = f;
                if (f === item.field) opt.selected = true;
                select.appendChild(opt);
            }
            select.addEventListener('change', () => { item.field = select.value; });
            row.appendChild(select);

            const removeBtn = document.createElement('button');
            removeBtn.className = 'groupby-remove';
            removeBtn.type = 'button';
            removeBtn.textContent = '\u00d7';
            removeBtn.addEventListener('click', () => this.#removeGroupBy(item.id));
            row.appendChild(removeBtn);

            container.appendChild(row);
        }
    }

    // ── Wizard: Output Config Generation ────────────────────────────────────

    #generateOutputConfig() {
        const config = this.#buildCardConfig();
        const json = JSON.stringify(config, null, 2);
        const output = this.shadowRoot.getElementById('output-config');
        if (output) output.textContent = json;
        // Also update the config view tab
        const configOutput = this.shadowRoot.getElementById('config-output');
        if (configOutput) configOutput.textContent = json;
    }

    #copyOutputConfig() {
        const output = this.shadowRoot.getElementById('output-config');
        if (!output || !output.textContent) {
            this.#setStatus('No config to copy — complete the wizard first');
            return;
        }
        navigator.clipboard.writeText(output.textContent).then(() => {
            this.#setStatus('Card configuration copied to clipboard');
        }).catch(() => {
            this.#setStatus('Failed to copy — try selecting and copying manually');
        });
    }

    #renderConfigView() {
        const config = this.#buildCardConfig();
        const el = this.shadowRoot.getElementById('config-output');
        if (el) el.textContent = JSON.stringify(config, null, 2);
    }

    #copyConfigView() {
        const el = this.shadowRoot.getElementById('config-output');
        if (!el || !el.textContent) {
            this.#setStatus('No config to copy');
            return;
        }
        navigator.clipboard.writeText(el.textContent).then(() => {
            this.#setStatus('Card config copied to clipboard');
        }).catch(() => {
            this.#setStatus('Failed to copy — try selecting and copying manually');
        });
    }

    #buildCardConfig() {
        const source = this.#selectedSource;
        if (!source) return {};
        const sd = this.shadowRoot;

        const config = {
            x: 0,
            y: 0,
            w: parseInt(sd.getElementById('custom-width')?.value) || 12,
            h: parseInt(sd.getElementById('custom-height')?.value) || 4,
            id: (source.name || source.path || 'widget').replace(/[^a-z0-9_]/gi, '_').toLowerCase(),
        };

        const title = sd.getElementById('custom-title')?.value?.trim();
        if (title) config.title = title;

        const isExportable = sd.getElementById('custom-exportable')?.value !== 'no';
        if (title) config.exportTitle = title;

        // Determine type
        if (this.#vizType === 'table') {
            config.type = 'hs-table';
            config.exportable = isExportable;
            config.view = 'table';
            const searchEnabled = sd.getElementById('custom-search')?.value === 'yes';
            if (searchEnabled) config.search = { enabled: true };
        } else if (this.#vizType === 'line' || this.#vizType === 'bar') {
            config.type = 'hs-chart';
            config.chartType = this.#vizType;
            config.exportable = isExportable;
        } else if (this.#vizType === 'metric') {
            config.type = 'hs-metric';
        }

        // Build src
        const isTimeSeries = this.#isTimeSeriesSource(source);
        const useTimeScopeTemplate = isTimeSeries && sd.getElementById('output-timescope-toggle')?.value !== 'no';
        const src = { path: useTimeScopeTemplate ? this.#buildTimeScopePath(source.path) : source.path };

        if (isTimeSeries) {
            src.timeSeries = true;
            src.applyTimeScopeFilters = true;

            // Add timeScopes array
            const scopeSelect = sd.getElementById('output-timescopes');
            if (scopeSelect) {
                const scopes = Array.from(scopeSelect.selectedOptions || []).map(o => o.value);
                if (scopes.length > 0) config.timeScopes = scopes;
            }
        }
        src.applyProviderFilters = true;

        // footerTitleTemplate for metric cards with timeScope
        if (this.#vizType === 'metric' && isTimeSeries && useTimeScopeTemplate) {
            config.footerTitleTemplate = "${session.get('timeScope') == 'month' ? 'last 30 days' : session.get('timeScope') == 'week' ? 'last 7 days' : session.get('timeScope') == 'day' ? 'last 24 hours' : session.get('timeScope') == 'quarter' ? 'current quarter' : 'all time'}";
        }

        // Aggregation
        if (this.#aggrOp) {
            const aggrEntry = { type: this.#aggrOp };
            if (this.#aggrField) aggrEntry.field = this.#aggrField;
            src.aggregate = [aggrEntry];
        }

        // GroupBy
        const activeGroupBy = this.#groupByFields.filter(g => g.field).map(g => g.field);
        if (activeGroupBy.length > 0) {
            src.groupby = activeGroupBy;
        }

        // Filters
        const validFilters = this.#filters.filter(f => f.field && f.op);
        if (validFilters.length > 0) {
            const filterExpr = validFilters.length === 1
                ? [validFilters[0].field, validFilters[0].op, validFilters[0].value]
                : validFilters.reduce((expr, f, i) => {
                    expr.push([f.field, f.op, f.value]);
                    if (i < validFilters.length - 1) expr.push('and');
                    return expr;
                }, []);
            src.filter = filterExpr;
        }

        config.src = src;

        // Chart axis config
        if (this.#vizType === 'line' || this.#vizType === 'bar') {
            const xAxis = sd.getElementById('viz-x-axis')?.value || '';
            const yAxisOpts = sd.getElementById('viz-y-axis');
            const yAxis = yAxisOpts ? Array.from(yAxisOpts.selectedOptions || []).map(o => o.value) : [];

            if (xAxis) config.xAxis = xAxis;
            if (yAxis.length > 0) config.yAxis = yAxis;
            if (yAxis.length > 1) config.multiLineChart = true;

            // Y-axis labels
            const yLabels = sd.getElementById('custom-y-labels')?.value?.trim();
            if (yLabels) {
                config.yAxisNames = yLabels.split(',').map(s => s.trim()).filter(Boolean);
            }

            config.chartOptions = {
                tooltip: { trigger: 'axis' },
                xAxis: { type: isTimeSeries ? 'time' : 'category', boundaryGap: this.#vizType === 'bar' },
                yAxis: { type: 'value' },
            };

            // Legend
            const legend = sd.getElementById('custom-legend')?.value;
            if (legend === 'show') config.chartOptions.legend = { show: true };
            else if (legend === 'hide') config.chartOptions.legend = { show: false };

            // Tooltip
            const tooltip = sd.getElementById('custom-tooltip')?.value;
            if (tooltip === 'item') config.chartOptions.tooltip = { trigger: 'item' };
            else if (tooltip === 'none') config.chartOptions.tooltip = { show: false };
        }

        // Metric config
        if (this.#vizType === 'metric') {
            const metricField = sd.getElementById('viz-metric-field')?.value || '';
            const suffix = sd.getElementById('viz-metric-suffix')?.value?.trim() || '';
            if (metricField) config.field = metricField;
            if (suffix) config.suffix = suffix;

            const metricFormatter = sd.getElementById('custom-metric-formatter')?.value;
            if (metricFormatter) config.valueFormatter = metricFormatter;

            const metricIcon = sd.getElementById('custom-metric-icon')?.value;
            if (metricIcon) config.icon = metricIcon;
        }

        // Table cols config — use column configs from customize step, exclude join-only fields
        if (this.#vizType === 'table') {
            const outputFields = this.#getOutputFieldIds();
            const colConfigs = this.#columnConfigs.length > 0
                ? this.#columnConfigs.filter(c => !this.#joinOnlyFields.has(c.field))
                : outputFields.map(f => ({ field: f, label: f, formatter: '', width: '' }));

            config.cols = colConfigs.map(c => {
                const col = {
                    id: c.field,
                    name: c.label || c.field,
                    field: c.field,
                    sortable: true,
                };
                if (c.formatter) col.formatter = c.formatter;
                if (c.width) col.width = c.width;
                return col;
            });

            const sortCol = sd.getElementById('custom-sort-col')?.value;
            const sortDir = sd.getElementById('custom-sort-dir')?.value || 'asc';
            if (sortCol) {
                config.defaultSort = { field: sortCol, direction: sortDir };
            }
        }

        // Adornments
        const validAdornments = this.#adornments.filter(a => a.sourceId && this.#isAdornmentKeyComplete(a) && a.fields?.size > 0);
        if (validAdornments.length > 0) {
            src.adornments = validAdornments.map(a => {
                const { key, source_key } = this.#getAdornmentKeyFields(a);
                const entry = {
                    source: a.sourceId,
                    key: key.length === 1 ? key[0] : key,
                    fields: Array.from(a.fields),
                };
                const keysMatch = key.length === source_key.length && key.every((k, i) => k === source_key[i]);
                if (!keysMatch) {
                    entry.source_key = source_key.length === 1 ? source_key[0] : source_key;
                }
                return entry;
            });
        }

        return config;
    }

    // ── Time-scope path template ────────────────────────────────────────────

    #buildTimeScopePath(originalPath) {
        // For rollup sources like "api/rollup/homesight/call_records_by_time/daily",
        // replace the trailing granularity with a session.get('timeScope') template expression
        const granularities = ['hourly', 'daily', 'monthly', 'quarterly'];
        const parts = originalPath.split('/');
        const lastPart = parts[parts.length - 1];

        if (granularities.includes(lastPart)) {
            const basePath = parts.slice(0, -1).join('/');
            const isStats = originalPath.includes('/stats/') && !originalPath.includes('/rollup/');
            if (isStats) {
                // stats sources switch between stats/... and rollup/stats/... for day scope
                return `${basePath}/\${session.get('timeScope') == 'all' ? 'monthly' : session.get('timeScope') == 'quarter' ? 'quarterly' : session.get('timeScope') == 'day' ? 'hourly' : 'daily'}`;
            }
            return `${basePath}/\${session.get('timeScope') == 'all' ? 'monthly' : session.get('timeScope') == 'quarter' ? 'quarterly' : session.get('timeScope') == 'day' ? 'hourly' : 'daily'}`;
        }
        // No trailing granularity — return as-is
        return originalPath;
    }

    #syncOutputTimeScopeSection() {
        const sd = this.shadowRoot;
        const section = sd.getElementById('output-timescope-section');
        if (!section) return;
        const isTimeSeries = this.#isTimeSeriesSource(this.#selectedSource);
        section.classList.toggle('hidden', !isTimeSeries);
    }

    // ── Export ──────────────────────────────────────────────────────────────

    async #exportData() {
        if (this.#results.length === 0) {
            this.#setStatus('No data to export — run a query first');
            return;
        }
        const sourceName = this.#selectedSource?.name || 'explorer';
        const cols = this.#grid ? this.#grid.getColumns() : [];

        // Sheet name max 31 chars in Excel
        const sheetName = sourceName.slice(-31).replace(/[\\/?*[\]:]/g, '_');
        
        const columns = cols.map((col, idx) => ({
            column: String.fromCharCode(65 + idx),
            title: col.name,
            type: String,
            width: 22
        }));
        
        const rows = this.#results.map(row => cols.map(col => row[col.field]));
        
        await writeExcelFile({
            sheets: [{
                sheet: sheetName,
                columns: columns,
                rows: rows
            }],
            fileName: `${sourceName.replace(/\./g, '_')}_export.xlsx`
        });
    }

    // ── UI helpers ──────────────────────────────────────────────────────────

    #renderAutoPreviewDefaults() {
        const container = this.shadowRoot.getElementById('auto-defaults');
        if (!container) {
            return;
        }

        container.innerHTML = '';
        if (!Array.isArray(this.#autoPreviewDefaults) || this.#autoPreviewDefaults.length < 1) {
            container.classList.add('hidden');
            return;
        }

        const label = document.createElement('span');
        label.className = 'auto-defaults-label';
        label.textContent = 'Auto filters:';
        container.appendChild(label);

        for (const item of this.#autoPreviewDefaults.slice(0, 6)) {
            const chip = document.createElement('button');
            chip.className = 'auto-default-chip';
            chip.type = 'button';
            chip.textContent = `${item.field}${item.op}${item.value}`;
            chip.title = `${item.reason}: ${item.field} ${item.op} ${item.value}`;
            if (item.filterId) {
                chip.addEventListener('click', () => this.#focusFilterRow(item.filterId));
            }
            container.appendChild(chip);
        }

        container.classList.remove('hidden');
    }

    #setStatus(msg) {
        const el = this.shadowRoot.getElementById('status-bar');
        if (el) el.textContent = msg;
    }

    #showSourceLoading(text = 'Loading\u2026') {
        const el = this.shadowRoot.getElementById('source-loading-indicator');
        const textEl = this.shadowRoot.getElementById('source-loading-text');
        if (el) el.classList.remove('hidden');
        if (textEl) textEl.textContent = text;
    }

    #hideSourceLoading() {
        const el = this.shadowRoot.getElementById('source-loading-indicator');
        if (el) el.classList.add('hidden');
    }

    #setResultsCount(count) {
        const el = this.shadowRoot.getElementById('results-count');
        if (el) el.textContent = count > 0 ? `${count} rows` : '';
        this.#syncCopyJsonButton();
    }

    #setRunning(running) {
        const btn = this.shadowRoot.getElementById('run-btn');
        if (!btn) return;
        btn.disabled = running;
        btn.innerHTML = running ? 'Running…' : '&#9654; Run';
    }

    async reloadData() {
        this.#closeControlsModal();
        this.#sources = [];
        this.#schema = null;
        this.#selectedSource = null;
        this.#selectedFields = new Set();
        this.#hasUserFieldSelection = false;
        this.#sourceSchemaOriginal = null;
        this.#filters = [];
        this.#aggrOp = '';
        this.#aggrField = '';
        this.#results = [];
        this.#granularity = '';
        this.#adornments = [];
        this.#autoPreviewDefaults = [];
        this.#pickerFilterId = null;
        this.#pickerField = '';
        this.#fieldValueOptionsCache = new Map();
        this.#showAllFields = true;
        this.#autoVisibleFieldIds = new Set();
        this.#resultsView = 'card';
        this.#schemaViewMode = 'summary';
        this.#activeHeaderField = '';
        this.#activeHeaderIndex = -1;
        this.#adornFieldPickerAdornmentId = null;
        this.#vizType = 'table';
        this.#groupByFields = [];
        this.#groupByIdCounter = 0;
        this.#columnConfigs = [];
        this.#joinOnlyFields = new Set();
        this.#keyMappingAdornmentId = null;
        this.#keyMappingDraft = [];
        this.#disposeChartPreview();
        // Close list-picker if open
        const picker = this.shadowRoot.querySelector('hs-list-picker');
        if (picker) picker.close();
        if (this.#fieldSelectionRefreshTimer) {
            clearTimeout(this.#fieldSelectionRefreshTimer);
            this.#fieldSelectionRefreshTimer = null;
        }

        this.#updateSourcePickerTrigger(null);
        const fieldSearch = this.shadowRoot.getElementById('field-search');
        const aggrOp = this.shadowRoot.getElementById('aggr-op');
        const aggrField = this.shadowRoot.getElementById('aggr-field');
        const granularity = this.shadowRoot.getElementById('granularity-select');
        if (fieldSearch) {
            fieldSearch.value = '';
        }
        if (aggrOp) {
            aggrOp.value = '';
        }
        if (aggrField) {
            aggrField.value = '';
        }
        if (granularity) {
            granularity.value = '';
        }

        this.#hideAdornShortcutMenu();
        this.#closeValuePicker();
        this.#closeAdornFieldPicker();
        this.#updateFieldsModeButtons();
        this.#renderAutoPreviewDefaults();
        this.#setResultsView('card');
        this.#syncControlsLauncherState();
        this.#renderFilterList();
        this.#renderFieldList([]);
        this.#renderAdornmentList();
        this.#setResultsCount(0);
        this.#renderSchemaView();
        this.#syncHeaderKeyboardState();
        if (this.#dataView) {
            this.#dataView.setItems([]);
        }

        // Reset wizard to step 1
        this.#navigateToStep('source');

        await this.#loadSources();
    }

    #close() {
        this.emit({ type: 'hs-close-explorer' });
    }
}

customElements.define('hs-explorer', HSExplorer);
